/** Documentul unui tronson, și geometria lui la front nominal.
 *
 *  UN TRONSON e modulul din care se compune o bară: două sau mai multe
 *  apartamente de o parte și de alta a unui coridor, uneori cu un nucleu de
 *  scară. Documentul e transcris din planșă (`architecture/tools/
 *  transcrie_pereti.py`) și stă în `catalog.tronson`; schema lui e
 *  `architecture/layout/catalog/tronsoane/tronsoane.schema.json`.
 *
 *  DE CE FIECARE COORDONATĂ E O PERECHE. Un modul se ÎNTINDE pe front: aceeași
 *  garsonieră stă pe 5,79 m sau pe 6,41 m, după cât a mai rămas din bară. Ca
 *  instanțierea să fie o aplicație liniară și nu o recompunere, fiecare
 *  coordonată e scrisă afin în întindere:
 *
 *      valoare = a + k · delta
 *
 *  `k` e PE CAMERĂ, nu pe vârf: o cameră care nu absoarbe se mută întreagă sau
 *  stă întreagă. Ce absoarbe scrie modulul, în `absorbers`.
 *
 *  AICI SE DESENEAZĂ LA delta = 0, adică la frontul NOMINAL — și nu din
 *  comoditate: e chiar cadrul pe care catalogul își verifică acoperirea
 *  (`_check_walls` compune anvelopa din `front.nominal_m` și compară cu
 *  `q[0]["a"]`). Un desen făcut la altă întindere ar arăta un modul pe care
 *  nicio probă nu l-a văzut.
 *
 *  TREI CADRE, fiindcă nici un colț, nici o casă nu sunt o bară:
 *
 *    * modul obișnuit — x de-a lungul coridorului, y transversal, **y = 0 e axa
 *      coridorului**, deci y merge de la −banda/2 la +banda/2 și cele două
 *      laturi de apartamente sunt `side: "A"` (y < 0) și `side: "B"` (y > 0);
 *    * colț — originea în colțul EXTERIOR al anvelopei, u pe bară, v pe aripă,
 *      amândouă pozitive; anvelopa e un pătrat din care lipsește crestătura din
 *      colțul intrând. Nu are `side: "B"`;
 *    * IZOLAT (`role: "standalone"`) — o locuință întreagă, care stă singură:
 *      nicio bară nu trece prin ea și niciun coridor n-o taie. Cadrul e al
 *      colțului — originea în colțul anvelopei, x și y pozitive —, iar anvelopa
 *      e dreptunghiul întreg, fără crestătură. Tot ce e în ea e `side: "A"`.
 *
 *      DE CE CADRUL COLȚULUI ȘI NU AL BAREI. Un modul de bară are y = 0 pe axa
 *      coridorului fiindcă acolo chiar e ceva: axa pe care se compune bara.
 *      Într-o casă nu e nimic acolo — ar fi o linie inventată prin mijlocul
 *      livingului, iar jumătate din camere ar cădea pe o «latură B» care nu
 *      există în document.
 */

/** O coordonată afină în întindere: `a + k·delta`. `k` lipsă înseamnă 0 —
 *  bucata nu se mișcă. */
export type Afin = { a: number; k?: number };
/** Un vârf: (x, y) sau (u, v) pe colț. */
export type VarfAfin = [Afin, Afin];

export type CameraDoc = {
  kind: string;
  /** NUMELE SCRIS PE CAMERĂ, când nu e cel al felului ei — v. schema.
   *
   *  Aici se ține un TIP NOU de spațiu fără să se inventeze un `kind`: felul
   *  rămâne canonic (motoarele îl citesc), iar numele e al omului. «Cramă» e o
   *  debara care se cheamă cramă, nu un fel pe care nu-l cunoaște nimeni. */
  label?: string;
  side: "A" | "B";
  area_m2?: number;
  poly: VarfAfin[];
  /** Al câtelea apartament din modul — necesar pe colț, unde patru locuințe
   *  împart un cadru și `side` nu le mai deosebește. */
  flat?: number;
  /** DESENATĂ CU MÂNA, nu dedusă din pereți.
   *
   *  Camerele se refac din rețeaua de ziduri la fiecare geometrie atinsă
   *  (v. `editor/camere::cuCamereDerivate`), fiindcă aia e singura care nu
   *  minte despre ce încăperi există. O zonă trasă de om — un duș în baie, o
   *  nișă — n-are însă ziduri ale ei: dedusă, ea pur și simplu nu există, iar
   *  la primul perete mișcat dispărea. Semnul ăsta o scoate din deducție:
   *  rămâne cum a fost desenată, iar camera peste care stă se taie în jurul
   *  ei. Cerut de arhitect (22.10.2026). */
  desenata?: boolean;
  /** CARE SPAȚIU EXTERIOR E, pe o cameră de fel `balcony`.
   *
   *  Vocabularul canonic are un singur fel de spațiu exterior, deci fără câmpul
   *  ăsta o logie și un balcon ies indistinct din document. Iar distincția
   *  hotărăște o cifră: un balcon în CONSOLĂ stă în afara benzii și nu intră în
   *  suprafața construită; o logie e scobită ÎN volum, deci e deja în amprentă
   *  și intră. V. schema catalogului, la aceeași cheie. */
  outdoor_kind?: "balcony" | "loggia";
  /** PEREȚII CARE O ÎNCONJOARĂ, ca `id`-uri din `plan.walls` — scriși de
   *  importul din imagine, unde se MĂSOARĂ (v. schema). Un perete dintre două
   *  camere e la amândouă. */
  walls?: string[];
  /** IDENTITATEA OBIECTULUI DIN SOLUȚIA ARHITECTURALĂ, când documentul e un
   *  nivel al unei soluții deschis în editor (v. `plan/nivelTronson.ts`), nu un
   *  șablon din catalog. La salvare, obiectul cu `eid` se rescrie PE LOC în
   *  documentul canonic, cu tot ce poartă acolo și editorul nu știe — iar cel
   *  fără `eid` se naște. Pe un tronson lipsește întotdeauna. */
  eid?: string;
};

/** SPAȚIUL EXTERIOR ALES pe o locuință — balcon în consolă, logie sau nimic.
 *
 *  Planșa desenează O configurație; celelalte două se scriu ca PETICE, aici.
 *  Alegerea se face în amonte, pe instanță (`positioning_plan`), și se aplică
 *  la instanțiere (`layout/from_tronsoane.py::apply_outdoor`) — deci editorul
 *  nu alege nimic: desenează configurația declarată de varianta implicită, iar
 *  peticele le pune la zi serverul, la salvare.
 *
 *  AICI E TIPAT DOAR CÂT SĂ NU SE PIARDĂ. Editorul trimite documentul întreg
 *  înapoi, deci un bloc netipat ar trece oricum prin `JSON.parse`/`stringify`;
 *  tipul e ca lucrurile care ATÂRNĂ de o cameră de balcon să se poată citi
 *  (cui îi aparține, cât costă o logie) fără un `as` la fiecare atingere. */
export type PeticExterior = {
  poly: VarfAfin[];
  area_m2: number;
  /** `sheet` = poligonul e citit (sau desenat) ca atare; `derived` = e convenția
   *  casei aplicată celeilalte alegeri. */
  origin: "sheet" | "derived";
  rooms?: CameraDoc[];
  walls?: PereteDoc[];
  /** Id-urile de pereți din `plan.walls` pe care alegerea asta îi scoate. */
  drops?: string[];
};

export type SpatiuExteriorDoc = {
  flat: number;
  side: "A" | "B";
  /** Cât se retrage livingul și peretele lui la logie. */
  recess_m?: number;
  balcony?: PeticExterior;
  loggia?: PeticExterior;
};

/** Felurile de perete, în ordinea în care le arată panoul de proprietăți. */
export const FELURI_PERETE = ["structural", "partition", "facade", "fire",
                              "parapet"] as const;
export type FelPerete = (typeof FELURI_PERETE)[number];

/** UN STRAT dintr-un perete: din ce e făcut și cât e de gros.
 *
 *  `kind` e din catalogul de straturi (`design/straturi.ts`), care e și cel din
 *  schema catalogului de tronsoane — cele două se țin egale de
 *  `test_layer_kinds_agree_with_the_editor`, fiindcă un strat pe care schema
 *  nu-l cunoaște oprește publicarea modulului, nu desenul lui. */
export type StratDoc = {
  kind: string;
  thickness_m: number;
};

export type PereteDoc = {
  id: string;
  /** RANGUL DE DESEN, scris de mână din «Adu în față» / «Trimite în spate».
   *
   *  Zidurile se desenează PESTE mobilă (v. `Desen`), dar între ele ordinea era
   *  cea din document și nu se putea schimba: două ziduri suprapuse se acopereau
   *  în ordinea în care fuseseră scrise. Cerut de arhitect (26.10.2026): «nu văd
   *  un buton de adu în față pe pereți, și ar trebui să existe». Lipsă = ordinea
   *  din document, ca până acum — același înțeles ca la `PiesaDoc.rang`. */
  rang?: number;
  /** `structural` (beton turnat, portant), `partition` (compartimentare),
   *  `facade` (anvelopă), `fire` (antifoc), `parapet`. */
  kind: FelPerete | string;
  /** `beton` sau `zidarie`. E singura distincție care se propagă în jos, la
   *  structură și la parcaj: un perete de beton e portant. */
  material: string;
  /** Materialul l-a pus IMPORTUL, nu planșa (rigipsul implicit al unui perete
   *  fără straturi desenate). Structura nu-și ia limita driftului dintr-o
   *  presupunere, deci câmpul se șterge când omul alege materialul sau pune
   *  straturi — atunci materialul e declarat. V. schema, la aceeași cheie. */
  material_presupus?: boolean;
  thickness_m: number;
  /** Înălțimea, de la pardoseala finită. NULL sau lipsă = până la placă, adică
   *  `storey_height_m` al modulului — cazul obișnuit. Se scrie doar când
   *  peretele chiar e mai scund: un parapet, un perete de atic. */
  height_m?: number | null;
  /** Axul peretelui CURB, în metri, neîntins. Doar împreună cu `bulge`. */
  axis?: [[number, number], [number, number]];
  /** Cât de curb: `tan(θ/4)`, convenția DXF. `poly` rămâne ce citește toată
   *  lumea; perechea asta spune din ce a fost făcut, ca editorul să poată
   *  schimba curbura fără să deducă raza dintr-un poligon cu 26 de vârfuri. */
  bulge?: number;
  /** STRATURILE, în ordine DE LA STÂNGA LA DREAPTA — v. `design/straturi.ts`
   *  pentru ce înseamnă stânga (mâna stângă a celui care merge pe desen de la
   *  `axis[0]` spre `axis[1]`).
   *
   *  LIPSA LOR NU E O LIPSĂ: un zid fără straturi e un zid omogen, din
   *  `material`, iar așa sunt toate cele transcrise din planșă. Straturile se
   *  scriu abia când cineva le pune, și atunci `thickness_m` E SUMA LOR — o
   *  măsură se scrie o singură dată, iar grosimea unui zid stratificat e
   *  chiar cât ocupă straturile lui. De-aia câmpul de grosime din panou se
   *  stinge cât timp zidul are straturi: cine vrea alt total schimbă un
   *  strat.
   *
   *  PORTANȚA RĂMÂNE A ZIDULUI, nu a stratului: `material` spune mai departe
   *  dacă zidul duce încărcare, fiindcă asta citesc structura și parcajul.
   *  Un strat de vată pusă pe un zid de beton nu-l face neportant. */
  layers?: StratDoc[];
  area_m2?: number;
  on_facade?: boolean;
  encloses_core?: boolean;
  poly: VarfAfin[];
};

export type GolDoc = {
  x: Afin;
  y: Afin;
  w_m?: number;
  h_m?: number;
  rot_deg?: number;
  /** În ce parte se deschide foaia, ca semn față de NORMALA golului. Lipsă =
   *  dedus (v. `spreCamera`); se scrie doar când cineva întoarce sensul de
   *  mână, fiindcă deducția e o convenție, nu o măsurătoare. */
  swing?: 1 | -1;
  /** ROLUL UȘII, cum îl știe schema catalogului: `entrance` = intrarea în
   *  locuință, `room` = ușă interioară, `fire` = ușă de incendiu. Lipsă =
   *  dedus din plan (v. `esteIntrare`); scris, bate deducția. Pe ferestre
   *  câmpul nu spune nimic. */
  kind?: "entrance" | "room" | "fire";
  /** MODELUL UȘII — v. `ModelUsa`. Lipsă = «single», o foaie cu balama. Se
   *  scrie când cineva alege alt model din paletă sau din panou. */
  model?: ModelUsa;
  /** ÎN CARE TOC E BALAMAUA, față de direcția golului (`rot_deg`): `start`
   *  = tocul de la capătul de început al golului, `end` = celălalt. Lipsă =
   *  `start`. E a doua jumătate a sensului unei uși: `swing` spune în ce
   *  parte a zidului baleiază foaia, `hinge` spune din stânga sau din
   *  dreapta — și amândouă se scriu, fiindcă amândouă se văd pe planșă și
   *  hotărăsc unde stă întrerupătorul și pe unde treci. */
  hinge?: Balama;
  /** LĂȚIMEA TOCULUI de-a lungul zidului, în metri. E o mărime a ușii, nu a
   *  desenului — de-aia stă în document și pleacă mai departe în soluție
   *  (`architecture.opening.frame_m`). Lipsă = implicitul rolului
   *  (v. `TOC_IMPLICIT`, `TOC_INTRARE`). */
  frame_m?: number;
  /** Peretele-gazdă, ca `id`. */
  wall?: string | null;
  u_m?: number | null;
  sill_m?: number | null;
  height_m?: number | null;
  parapet_m?: number;
  block?: string;
  origin?: "cut" | "block";
  /** IDENTITATEA OBIECTULUI DIN SOLUȚIA ARHITECTURALĂ, când documentul e un
   *  nivel al unei soluții deschis în editor (v. `plan/nivelTronson.ts`), nu un
   *  șablon din catalog. La salvare, obiectul cu `eid` se rescrie PE LOC în
   *  documentul canonic, cu tot ce poartă acolo și editorul nu știe — iar cel
   *  fără `eid` se naște. Pe un tronson lipsește întotdeauna. */
  eid?: string;
};

export type PiesaDoc = {
  kind: string;
  x: Afin;
  y: Afin;
  w_m?: number;
  h_m?: number;
  /** A TREIA MĂSURĂ a piesei, în metri — lungime × lățime × ÎNĂLȚIME.
   *
   *  Lipsă = înălțimea de catalog a felului (`FelPiesa.inaltime`). Se scrie
   *  numai când piesa pusă e alta decât cea obișnuită — un dulap până în
   *  tavan, o canapea joasă.
   *
   *  DECLARAȚIE, NU GEOMETRIE, deocamdată: macheta așază modelele CC0, fiecare
   *  cu înălțimea lui. Cifra se scrie și se citește; ce desenează macheta nu
   *  atârnă încă de ea. */
  height_m?: number;
  rot_deg?: number;
  /** OGLINDIREA PIESEI, pe axa ei lungă (`flip_x`, stânga-dreapta) sau pe cea
   *  scurtă (`flip_y`, față-spate), aplicată DUPĂ rotire.
   *
   *  Nu e o rotire deghizată, și de-aia e un câmp aparte: o bucătărie în L, un
   *  colț de canapea sau un pat cu noptiera pe o singură parte au o MÂNĂ, iar
   *  mâna cealaltă nu se obține învârtind piesa — oricât ai roti un L, brațul
   *  scurt rămâne de aceeași parte. Amândouă deodată dau chiar 180°. */
  flip_x?: boolean;
  flip_y?: boolean;
  /** ANSAMBLUL din care face parte piesa — masa cu scaunele ei, de pildă.
   *
   *  Piesele cu același `grup` se mută, se rotesc, se șterg și se dublează
   *  ÎMPREUNĂ, iar un click pe oricare dintre ele le alege pe toate. Lipsă =
   *  piesă de sine stătătoare, cazul aproape oricărei mobile.
   *
   *  DE CE UN ȘIR, ȘI NU O PIESĂ COMPUSĂ CU SCAUNELE DESENATE ÎN EA. Fiindcă
   *  asta s-a mai încercat o dată și s-a scos: `Masa` își desena singură
   *  scaunele, iar pe un document care le avea și scrise ca piese se vedeau
   *  două rânduri unul peste altul («the chairs of the dining table are
   *  overlapping»). Un scaun rămâne deci o PIESĂ — se numără în extrase, se
   *  mută singur după ce ansamblul e desfăcut, și ține locul ocupat pentru
   *  mobilarea automată. `grup` spune doar că acum se mișcă împreună.
   *
   *  ID-UL NU ÎNSEAMNĂ NIMIC în afară de egalitate: două piese sunt în același
   *  ansamblu dacă au același șir. Se cere doar să nu se izbească de al altui
   *  ansamblu la dublare sau la lipire (v. `grupNou`). */
  grup?: string;
  /** RANGUL DE DESEN, scris de mână: cine e deasupra cui.
   *
   *  Lipsă = rangul FELULUI (`arhMobilierPlan::rangDesen`: covoarele dedesubt,
   *  blaturile peste ele, aparatele peste blaturi) — regula care ține fără ca
   *  nimeni să ceară ceva. Scris, îl bate: e hotărârea omului care a apăsat
   *  «Adu în față» sau «Trimite în spate» fiindcă voia să vadă o piesă peste
   *  alta, sau ce e sub ea.
   *
   *  E un ÎNTREG OARECARE, nu o poziție în listă: se compară între piese și
   *  atât. Ordinea din document rămâne ce era — pe ea merg indicii selecției,
   *  ai aparatelor legate (`ElectricDoc.pentru`) și orice altceva ține minte
   *  „a câta piesă”; o reordonare a listei le-ar fi rupt pe toate deodată. */
  rang?: number;
  /** Cota tălpii piesei, de la pardoseala finită. Lipsă = 0, adică pe jos —
   *  cazul aproape oricărei mobile. Se scrie pentru ce atârnă: televizorul de
   *  pe perete, corpul suspendat de bucătărie, hota. Fără ea, macheta le-ar
   *  așeza pe podea sub corpul peste care trebuie să stea. */
  z_m?: number;
  /** RAMPELE SCĂRII, când scara a fost DESENATĂ, nu pusă ca o cutie.
   *
   *  Fiecare rampă e linia ei de urcare — mijlocul ei —, de la primul la
   *  ultimul nas de treaptă, în coordonatele planului. Lățimea e a scării
   *  întregi, nu a fiecărei rampe: în Revit o tragi ca linie, iar lățimea stă
   *  în tipul de scară (113, apoi 110, în videoul din 28.09.2026).
   *
   *  DE CE SE SCRIU, când tot restul desenului se derivă. Pentru că rampele
   *  sunt chiar gestul omului: unde a început, încotro a dus, unde s-a oprit.
   *  Din ele ies podestele, cutia și numărul de trepte al fiecărei rampe — dar
   *  ele însele nu ies din nimic. O scară fără `scara` e una pusă ca piesă (sau
   *  adusă din import): se desenează mai departe din cutie, ca până acum.
   *
   *  `x`, `y`, `w_m`, `h_m` rămân scrise și la scara desenată — sunt cutia ei,
   *  deci o poți apuca, muta și roti ca pe orice piesă. */
  scara?: {
    /** Liniile de urcare, în ordinea tragerii. */
    rampe: { de: [number, number]; la: [number, number] }[];
    /** Lățimea rampei, în metri. */
    latime_m: number;
    /** ADÂNCIMEA TREPTEI, când e alta decât cea obișnuită (0,28). Scrisă, ea
     *  hotărăște câte trepte încap pe lungimea rampei. */
    treapta_m?: number;
    /** ÎNĂLȚIMEA CONTRATREPTEI. Lipsă = nivelul împărțit la câte trepte sunt —
     *  cazul obișnuit. Scrisă, e hotărârea omului: o scară poate urca la altă
     *  cotă decât tavanul nivelului (un podium, o mezaninăn). */
    pas_m?: number;
    /** ADÂNCIMEA PODESTULUI. Lipsă = cât o rampă e de lată, regula de planșă. */
    podest_m?: number;
    /** FORMA UNUI PODEST, când nu e cea dedusă din rampe.
     *
     *  Podestul era o CIFRĂ — o adâncime —, iar forma lui ieșea întotdeauna
     *  dreptunghiulară, între capetele a două rampe. Pe planșele cu fațadă
     *  oblică el nu e dreptunghi: urmează fațada și ocolește puțul liftului,
     *  cu cinci sau șase laturi. Cerut de arhitect (13.10.2026): «să suporte
     *  podesturi de shape-uri diferite… să adaug puncte, să le trag».
     *
     *  `k` e al câtelea podest (între rampa `k` și `k+1`), iar `contur` e
     *  poligonul lui în coordonatele PLANULUI, ca rampele. Lipsă = se deduce
     *  mai departe din rampe, deci toate scările scrise până acum se desenează
     *  exact ca până acum. */
    podeste?: { k: number; contur: [number, number][] }[];
  };
};

/** FINISAJELE UNEI CAMERE — pardoseala ei și tencuiala pereților ei.
 *
 *  E O DECIZIE, nu o convenție de desen, și de-aia se scrie. `arhMaterial`
 *  alege de mult o pardoseală după felul camerei (parchet la dormitor, gresie
 *  la baie) — dar aia e o REPREZENTARE: planșa arată lemn fiindcă acolo se
 *  doarme, nu fiindcă cineva a ales stejar. Aici cineva chiar alege, deci
 *  alegerea trebuie să urce în document, altfel e adevărată doar cât ține
 *  pagina deschisă.
 *
 *  PE CAMERĂ, prin poziția ei în `plan.rooms`. Nu pe `kind`: două dormitoare
 *  ale aceluiași apartament au voie să aibă pardoseli diferite, iar un finisaj
 *  scris pe fel le-ar lega pe toate. Camerele nespuse iau implicitul felului
 *  lor (v. `design/finisaje`), deci lista e scurtă: are în ea doar ce s-a
 *  schimbat de mână.
 *
 *  NU INTRĂ ÎN ARIE și nu intră în `alpha` — ca și mobilierul. Motoarele nu o
 *  citesc; o citesc planul, macheta și, cândva, antemăsurătoarea. */
export type FinisajDoc = {
  /** Camera, ca poziție în `plan.rooms`. */
  room: number;
  /** Cheia din catalogul de pardoseli. Lipsă = implicitul felului camerei. */
  floor?: string;
  /** Cheia din catalogul de finisaje de perete. */
  wall?: string;
};

/** UN APARAT ELECTRIC: o priză, un întrerupător, un corp de iluminat, tabloul.
 *
 *  DE CE STĂ ÎN DOCUMENT ȘI NU SE DEDUCE. O priză se pune unde se pune pentru
 *  că acolo stă patul — adică e o consecință a mobilării, nu a geometriei. Doar
 *  că mobilarea la rândul ei se poate schimba de mână, iar un aparat care s-ar
 *  recalcula din felul camerei ar sări de pe perete de fiecare dată când cineva
 *  mută o noptieră. Deci se pune o dată (de mână sau cu bagheta) și rămâne pus.
 *
 *  ÎNĂLȚIMEA DE MONTAJ, `h_m`, e de la PARDOSEALA FINITĂ, ca orice cotă de
 *  instalație. Lipsă = implicitul felului (v. `design/electric::APARATE`), ca
 *  documentul să nu poarte de 40 de ori aceeași cifră de 0,30 m.
 *
 *  TRASEUL FIRELOR NU SE SCRIE. El se calculează din poziții, din circuit și
 *  din pereți (`design/electric::trasee`) — e o consecință, iar o consecință
 *  scrisă în document e o a doua părere care se desparte de prima la primul
 *  perete mutat. */
export type ElectricDoc = {
  /** `priza`, `intrerupator`, `plafoniera`, `tablou`… — v. `design/electric`. */
  kind: string;
  x: Afin;
  y: Afin;
  /** Înălțimea de montaj de la pardoseala finită. Lipsă = implicitul felului. */
  h_m?: number;
  rot_deg?: number;
  /** Circuitul din care face parte: `lumina`, `prize`, `forta`, `date`.
   *  Lipsă = cel implicit al felului. */
  circuit?: string;
  /** PIESA DE MOBILIER CARE-L JUSTIFICĂ — poziția ei în `plan.furniture`.
   *
   *  O priză de noptieră nu e la o coordonată, e la capul patului: coordonata e
   *  o consecință. Scrisă, legătura face ca mutarea patului să ducă priza cu el
   *  — în timp real, cât e trasă piesa pe pânză (v. `langaMobila`). Lipsă =
   *  aparat de sine stătător: plafoniera, întrerupătorul de lângă ușă, tot ce a
   *  pus omul cu mâna.
   *
   *  LEGĂTURA SE RUPE LA PRIMA MUTARE CU MÂNA. Cine trage priza în altă parte a
   *  spus unde o vrea, iar o regulă care i-ar aduce-o înapoi la următoarea
   *  atingere a canapelei ar fi un editor care nu ascultă. */
  pentru?: number;
  /** De ce parte a piesei stă: `stanga`, `dreapta` sau `spate`. Doar împreună
   *  cu `pentru`. Din geometrie nu se poate deduce — două prize de o parte și
   *  de alta a patului sunt același motiv văzut în oglindă. */
  parte?: "stanga" | "dreapta" | "spate";
  /** OGLINDIREA SIMBOLULUI, ca la mobilier (`FurnitureDoc::flip_x`), aplicată
   *  DUPĂ rotire.
   *
   *  Simbolurile din normă au o mână: pârghia întrerupătorului urcă spre
   *  dreapta, a doua pârghie a unui dublu stă la dreapta primei, jumătatea
   *  hașurată a tabloului e la stânga. Rotirea nu le schimbă mâna — un
   *  întrerupător întors cu 180° are pârghia în jos, nu în oglindă —, iar pe un
   *  perete anume mâna contează: pârghia care urcă spre tocul ușii se desenează
   *  peste el.
   *
   *  Pe simbolurile rotunde (plafoniera, spotul) nu se vede nimic, și n-are de
   *  ce: sunt simetrice, exact ca o piesă de mobilier aproape simetrică.
   *
   *  Simbolurile arhitectului (`FelAparat::picior`) stau mereu în cameră: pe ele
   *  `flip_y` nu are efect. */
  flip_x?: boolean;
  flip_y?: boolean;
  /** A mounting box's devices (`kind: "doza"`), catalogue kinds in the order of
   *  their row on the plan. The modules and the default height follow from
   *  the catalogue, so they are not written. */
  devices?: string[];
};

export type VariantaDoc = {
  id: string;
  outdoor: string[];
  useful_m2: number[];
  default?: boolean;
};

export type TronsonDoc = {
  schema_version: string;
  id: string;
  label?: string;
  kind: string;
  role: "end" | "mid" | "core" | "corner-L" | "corner-T" | "standalone";
  band_depth_m: number;
  /** Înălțimea liberă a nivelului. Lipsă = `H_NIVEL`. */
  storey_height_m?: number;
  front: { nominal_m: number; min_m: number; max_m: number; rigid?: boolean };
  /** DE UNDE VINE DESENUL. Scrisă la naștere și purtată mai departe: planșa
   *  după care s-a transcris, cum s-a aflat scara, și ce s-a mai schimbat pe
   *  parcurs. Cine deschide un modul trebuie să poată răspunde «de unde știm
   *  asta?» fără să întrebe pe cineva. */
  provenance?: {
    /** Planșa sau drumul: «Bd. Timișoara 12-14 … planșa A05, 1:100», sau
     *  «import din imagine». */
    source?: string;
    /** Cum s-a aflat scara: «41.67 px/m din cota 8.00 m». */
    measured?: string;
    note?: string;
    dwg_frame?: string;
  };
  absorbers?: { side: string; room: string; max_delta_m: number }[];
  mirror?: "exact" | "none";
  core?: {
    front_m: number; stair_depth_m?: number; lift_depth_m?: number;
    lifts?: number; wall_t_m?: number;
  } | null;
  sides: Record<string, {
    flats: { camere: number; kitchen?: string; rooms: Record<string, number> }[];
  }>;
  variants: VariantaDoc[];
  plan?: {
    frame?: string;
    source?: string;
    measured_front_m?: number;
    measured_band_m?: number;
    measured_leg_a_m?: number;
    measured_leg_b_m?: number;
    notch_m?: number;
    rooms: CameraDoc[];
    walls?: PereteDoc[];
    doors?: GolDoc[];
    windows?: GolDoc[];
    /** PRAGURILE: liniile pe care se schimbă camera fără să fie zid — trecerea
     *  liberă dintre living și hol, fâșia de câțiva centimetri rămasă între
     *  două camere. Nu se desenează și nu urcă în machetă; țin doar camerele
     *  despărțite când se derivă din pereți. Vezi `architecture/tools/praguri.py`. */
    thresholds?: VarfAfin[][];
    /** MOBILIEREA DESENATĂ DE MÂNĂ, peste cea dedusă din felul camerei. Adnotare
     *  de desen: nu intră în arie, nu intră în `alpha`, motoarele n-o citesc. */
    furniture?: PiesaDoc[];
    /** Finisajele alese pe cameră. Cele nespuse iau implicitul felului lor. */
    finishes?: FinisajDoc[];
    /** AXELE HOTĂRÂTE DE OM, peste cele deduse din ziduri.
     *
     *  Axele nu se scriu: se deduc din pereți la fiecare desen (v.
     *  `editor/model::axele`), iar literele, cifrele și cotele dintre ele ies
     *  din mulțimea asta, în ordine. De-aia aici nu stau AXELE, ci cele două
     *  abateri de la deducție — și tocmai de-aia numerotarea se reface singură
     *  când se adaugă sau se scoate una.
     *
     *    * `ascuns: true` — axa dedusă de pe poziția aia nu se mai desenează.
     *      Zidurile ei rămân: o axă ștearsă e o hotărâre de trasare, nu o
     *      ștergere de pereți.
     *    * fără `ascuns` — se aduce o axă acolo unde geometria n-ar fi dat una
     *      (un perete mai scurt decât pragul, de pildă). */
    axes?: { axa: 0 | 1; valoare: number; ascuns?: boolean }[];
    /** Prizele, întrerupătoarele, corpurile de iluminat și tablourile. */
    electrical?: ElectricDoc[];
    /** CONTURUL MODULULUI, scris de mână — poligonul amprentei lui.
     *
     *  LIPSEȘTE PE MODULELE TRANSCRISE, și atunci se calculează din cifre:
     *  `front.nominal_m` × `band_depth_m` pe un modul drept, `measured_leg_a_m`
     *  și `notch_m` pe un colț (v. `anvelopa`). Deducția aia e bună cât timp
     *  conturul CHIAR e un dreptunghi sau un pătrat cu crestătură — adică pentru
     *  tot ce vine din planșă.
     *
     *  Se scrie când cineva trage de o latură sau de un colț al conturului. De
     *  atunci forma nu mai vine din cifre, fiindcă nu mai poate: un contur cu
     *  șapte laturi nu se descrie cu două numere. Cifrele rămân însă adevărate
     *  despre altceva și se recalculează din poligon — `front.nominal_m` e pasul
     *  la care începe modulul următor, iar el e chiar întinderea conturului pe x.
     *
     *  NU E AFIN. Un contur tras de mână nu se mai întinde: `k` ar fi trebuit
     *  ghicit pentru fiecare vârf nou, iar un contur care se întinde altfel
     *  decât declară `absorbers` e o probă care cade la încărcare. Modulul care
     *  are contur scris se așază, nu se întinde. */
    outline?: Pt[];
    /** Spațiul exterior al fiecărei locuințe, ca alegere — v. `SpatiuExteriorDoc`. */
    outdoor?: SpatiuExteriorDoc[];
    /** Unde stă scara: `[u0, v0, u1, v1]`, în metri, neîntins. */
    core_rect?: number[];
    /** `eid` doar pe un nivel de soluție — v. `CameraDoc.eid`. */
    core_parts?: { kind: string; poly: number[][]; eid?: string }[];
    /** COTELE PUSE DE MÂNĂ — v. `CotaDoc`. */
    dims?: { a: [number, number]; b: [number, number] }[];
    /** CÂT SE DESENEAZĂ dintr-o axă, pe numele ei: capătul de jos și cel de
     *  sus, în lungul axei.
     *
     *  E o măsură de ASPECT, nu de proiect: axa rămâne aceeași linie, cu
     *  aceiași pereți pe ea, oricât de scurt ar fi desenată. Cerut de arhitect
     *  (15.10.2026): «să putem micșora aspectul axelor… funcționalitatea
     *  rămâne la fel». Lipsă = cât o întind pereții ei, ca până acum. */
    axes_len?: { nume: string; jos: number; sus: number }[];
    /** AXELE BLOCATE, pe nume.
     *
     *  O axă blocată nu se mai mută — nici trasă cu mâna, nici împinsă din
     *  tastatură. Cerut de arhitect (26.10.2026): «să se poată bloca axele să
     *  nu se mai miște; să existe buton de blochează/deblochează pe fiecare
     *  axă». Pe un plan așezat, axele de trasare sunt reperul după care se
     *  măsoară tot restul: odată puse, ele se ating din greșeală mult mai des
     *  decât dinadins.
     *
     *  PE NUME, ca `axes_len`, și din același motiv: axele se DEDUC din ziduri
     *  la fiecare desenare (v. `model::axele`), deci n-au un indice al lor pe
     *  care să se poată scrie ceva. */
    axes_blocate?: string[];
    /** COTELE SOCOTITE care NU se mai desenează.
     *
     *  «Există cote preluate din desene care nu pot fi șterse — toate ar trebui
     *  să aibă opțiunea să poată fi șterse» (15.10.2026). O cotă socotită nu se
     *  poate șterge din document, fiindcă nu e în el: se naște din nou la
     *  fiecare desenare. Deci se ține minte că a fost STINSĂ, pe cheia ei —
     *  capetele, rotunjite la centimetru (v. `cheiaCotei`). */
    dims_stinse?: string[];
    /** ZIDURILE CĂRORA OMUL LE-A LĂSAT CAPETELE LIBERE, ca `id`-uri.
     *
     *  Conturul camerei se oprește cu 6 cm înainte de capătul unui zid care
     *  iese în gol, fiindcă la derivare zidurile se scad LUNGITE, ca îmbinările
     *  lor să se închidă (v. `editor/camere::lungit`). Acolo unde capătul n-are
     *  ce închide, lungirea nu face decât să mănânce din cameră — iar omul o
     *  poate scoate, zid cu zid, de pe planșă.
     *
     *  SE SCRIE PE NUME, nu pe indice: zidurile se taie și se renumerotează la
     *  fiecare tragere (v. `imparteZidulLaCamera`), iar un indice ar fi arătat
     *  mâine spre alt perete. */
    capete_libere?: string[];
  };
};

// ------------------------------------------------------------- geometria ---

export type Pt = [number, number];

/** Aria unui inel, NEROTUNJITĂ — formula lui Gauss.
 *
 *  Separată de `arieInelului` fiindcă aria unei camere e o SUMĂ: poligonul plus
 *  pragurile ușilor ei (v. `Prag`). Rotunjit fiecare termen, suma ar putea
 *  cădea cu un centimetru pătrat lângă cea a serverului, care adună întâi. */
export function arieBruta(ring: readonly Pt[]): number {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s) / 2;
}

/** Aria unui inel, rotunjită la centimetrul pătrat. DOAR DE AFIȘAT: cea care
 *  se salvează o pune serverul, ca să existe o singură implementare a ei. */
export function arieInelului(ring: readonly Pt[]): number {
  return Math.round(arieBruta(ring) * 100) / 100;
}

/** ARIA CONSTRUITĂ A UNEI CAMERE: până la JUMĂTATEA zidului dintre camere.
 *
 *  Cerut de arhitect (13.10.2026): «camere suprafață utilă (o avem) și aria
 *  construită — să se ducă pe jumătatea peretelui comun dintre camere, adică
 *  să calculeze jumătatea aceea».
 *
 *  REGULA, LATURĂ CU LATURĂ, fiindcă nu toate laturile unei camere sunt la
 *  fel:
 *
 *    • zidul care desparte DOUĂ CAMERE se împarte în două — fiecare îl poartă
 *      pe jumătate, și așa cele două arii nu se calcă una pe alta;
 *    • zidul care dă AFARĂ se ia ÎNTREG, până la fața lui exterioară. Luat și
 *      el pe jumătate, suma ariilor construite ale camerelor n-ar mai fi aria
 *      construită a modulului, ci ea minus un brâu de o jumătate de fațadă de
 *      jur împrejur — iar aria construită se măsoară tocmai pe conturul de
 *      afară;
 *    • o latură fără niciun zid — hotarul desenat dintre un living și locul de
 *      luat masa, muchia unui balcon — nu adaugă nimic: nu există zid de
 *      împărțit.
 *
 *  O FAȚADĂ E MAI MULTE FOI LIPITE (miez + termoizolație: v. `feteleGazdei`),
 *  deci drumul spre afară trece prin ele una după alta până dă de aer. Măsurat
 *  numai pe prima, aria construită s-ar fi oprit în mijlocul peretelui.
 *
 *  Socotită pe GEOMETRIA DESENATĂ, ca aria utilă — nu citită din document:
 *  tragi un zid, se schimbă amândouă (v. nota de la `arie`). */
const PAS_SONDA = 0.02;          // 2 cm: cât se iese din cameră ca să se vadă zidul
const FOI_MAX = 6;               // câte foi lipite se străbat într-o fațadă
/** Mai gros de-atât nu e un zid, e o măsurătoare care a luat-o razna: raza a
 *  intrat într-un zid PERPENDICULAR pe latură — unul care se opintește în ea —
 *  și a ieșit tocmai la capătul LUI, la câțiva metri. */
const GROSIME_MAX = 1.2;
/** În câte locuri se măsoară o latură. UNUL SINGUR — mijlocul — nu ajunge, și
 *  asta a costat două reparații: pe holul lui M3 mijlocul laturii din stânga
 *  cade chiar unde se opintește un perete despărțitor, iar mijlocul celei din
 *  dreapta cade într-un GOL DE UȘĂ, unde nu e niciun zid. Se iau șapte sonde și
 *  se ține cea de la mijlocul șirului: ușile și întâlnirile de ziduri sunt
 *  puține, deci nu urnesc mediana. */
const SONDE_PE_LATURA = 7;

/** UNDE IESE raza `(m, n)` dintr-un inel, pornind de la `t0`. `null` = nu iese.
 *
 *  Aici întâi se lua «cel mai depărtat vârf al zidului pe direcția ieșirii»,
 *  ceea ce e grosimea lui numai dacă zidul stă DE-A CURMEZIȘUL razei. Pe o
 *  latură care se uită spre CAPĂTUL unui zid — scobitura de la capătul holului
 *  lui M3 — cel mai depărtat vârf e la celălalt capăt al fațadei, iar
 *  «grosimea» ieșea de șase metri. Raza taie inelul acolo unde chiar îl taie. */
function iesireaDinInel(m: Pt, n: Pt, inel: readonly Pt[],
                        t0: number): number | null {
  let cel: number | null = null;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i], b = inel[(i + 1) % inel.length];
    const ex = b[0] - a[0], ey = b[1] - a[1];
    const jos = n[0] * ey - n[1] * ex;
    if (Math.abs(jos) < 1e-12) continue;            // latura e paralelă cu raza
    const t = ((a[0] - m[0]) * ey - (a[1] - m[1]) * ex) / jos;
    if (t <= t0 + 1e-9) continue;
    const px = m[0] + n[0] * t - a[0], py = m[1] + n[1] * t - a[1];
    const u = (px * ex + py * ey) / (ex * ex + ey * ey);
    if (u < -1e-9 || u > 1 + 1e-9) continue;
    if (cel === null || t < cel) cel = t;
  }
  return cel;
}

/** Cât ține zidăria dincolo de latura asta, și dacă de partea cealaltă e o
 *  altă cameră. `null` = nu e niciun zid acolo. */
/** CUTIA UNUI INEL, ȚINUTĂ MINTE PE INELUL ÎNSUȘI.
 *
 *  `arieConstruita` întreabă, pentru fiecare sondă, CARE zid cuprinde punctul —
 *  iar întrebarea asta se pune de sute de mii de ori pe un modul mare: 139 de
 *  camere × laturile lor × 7 sonde, fiecare măturând toți cei 215 pereți și
 *  toate celelalte camere cu `inInel`, adică cu un test punct-în-poligon
 *  întreg. Măsurat pe «23 IZOLAT LUXURY», socoteala asta singură ținea 203 din
 *  cele 220 ms ale lui `deriva` — iar `deriva` se reface la FIECARE pas al unei
 *  trageri de perete, deci ea e jumătatea mare a lagului de acolo.
 *
 *  Cutia e o SITĂ, nu un răspuns: dacă punctul cade în afara ei, inelul sigur
 *  nu-l cuprinde, deci `inInel` nici nu se mai cheamă; dacă e înăuntru, se
 *  cheamă ca până acum. Rezultatul e identic, bit cu bit — se schimbă doar cât
 *  se lucrează ca să se ajungă la el.
 *
 *  PE `WeakMap`, nu pe un argument în plus: aceleași obiecte-inel se întreabă
 *  de sute de ori într-o singură derivare (pereții sunt aceiași pentru toate
 *  camerele), iar un parametru nou ar fi cerut ca fiecare chemător să care
 *  indexul după el. La pasul următor al tragerii documentul e altul, deci și
 *  inelele — cheile vechi se duc singure. */
const cutiiInel = new WeakMap<object, [number, number, number, number]>();
function cutiaInel(r: readonly Pt[]): [number, number, number, number] {
  const gata = cutiiInel.get(r as object);
  if (gata) return gata;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const q of r) {
    if (q[0] < x0) x0 = q[0];
    if (q[0] > x1) x1 = q[0];
    if (q[1] < y0) y0 = q[1];
    if (q[1] > y1) y1 = q[1];
  }
  const c: [number, number, number, number] = [x0, y0, x1, y1];
  cutiiInel.set(r as object, c);
  return c;
}
/** Punctul poate fi în inel? (cutie pe punct — sita de mai sus.)
 *
 *  INVARIANTUL, și singurul: ce acceptă `inInel` trebuie să accepte și sita.
 *  Marginile sunt deci INCLUSE — un `<` în locul unui `<=` ar arunca puncte pe
 *  muchia cutiei, adică exact pe fețele zidurilor, acolo unde cad sondele. E
 *  exportată ca să poată fi probată pe invariantul ăsta, nu fiindcă ar avea
 *  cineva nevoie de ea (v. `sitaCutiei.test.ts`). */
export function poateFiIn(p: Pt, r: readonly Pt[]): boolean {
  const c = cutiaInel(r);
  return p[0] >= c[0] && p[0] <= c[2] && p[1] >= c[1] && p[1] <= c[3];
}

function zidulDeDincolo(
  m: Pt, n: Pt, pereti: readonly { inel: Pt[] }[],
  celelalte: readonly (readonly Pt[])[],
): { gros: number; intreCamere: boolean } | null {
  let gros = 0;
  for (let foaie = 0; foaie < FOI_MAX && gros < GROSIME_MAX; foaie++) {
    const q: Pt = [m[0] + n[0] * (gros + PAS_SONDA),
                   m[1] + n[1] * (gros + PAS_SONDA)];
    const z = pereti.find((w) => w.inel.length >= 3
      && poateFiIn(q, w.inel) && inInel(q, w.inel));
    if (!z) break;
    const pana = iesireaDinInel(m, n, z.inel, gros + PAS_SONDA / 2);
    if (pana === null || pana <= gros + 1e-6) break;   // n-a înaintat
    gros = pana;
  }
  if (gros <= 1e-6 || gros > GROSIME_MAX) return null;
  const dincolo: Pt = [m[0] + n[0] * (gros + PAS_SONDA),
                       m[1] + n[1] * (gros + PAS_SONDA)];
  return { gros,
    intreCamere: celelalte.some((r) => poateFiIn(dincolo, r)
      && inInel(dincolo, r)) };
}

/** REGIUNEA CONSTRUITĂ, ca REUNIUNE, nu ca inel împins în afară.
 *
 *  Împingerea laturilor și tăierea lor două câte două e socoteala curată — și
 *  e greșită aici. Un hol al lui M3 are la capăt o scobitură de 7,5 cm, mai
 *  mică decât zidul care-l împrejmuiește: împinse cu 10–35 cm, laturile ei
 *  se încalecă, iar poligonul iese în fundă. Formula lui Gauss pe o fundă
 *  scade o bucată în loc s-o adune — de-aia holul lui M3 ieșea 22,85 m² la
 *  4,92 utili, iar al lui M5 ieșea mai MIC decât utilul.
 *
 *  Reuniunea nu are cazul ăsta: camera, plus câte o fâșie pe fiecare latură
 *  (latura × cât îi revine din zid), plus câte un petic în fiecare colț —
 *  acolo unde două fâșii se întâlnesc rămâne un pătrat de zid care e tot al
 *  camerei. Ce se încalecă se numără o dată, fiindcă asta face o reuniune. */
function regiuneaCladita(ring: readonly Pt[], dep: readonly number[]): number {
  const n = ring.length;
  const norm = ring.map((_, i) => normalaInAfara(ring, i));
  const inchis = (p: Pt[]): Pt[] => [...p, p[0]];
  const bucati: Pt[][][] = [[inchis(ring.map((q) => [q[0], q[1]]))]];
  for (let i = 0; i < n; i++) {
    const d = dep[i] ?? 0;
    if (d <= 0) continue;
    const a = ring[i], b = ring[(i + 1) % n], q = norm[i];
    bucati.push([inchis([[a[0], a[1]], [b[0], b[1]],
                         [b[0] + q[0] * d, b[1] + q[1] * d],
                         [a[0] + q[0] * d, a[1] + q[1] * d]])]);
    // colțul dintre fâșia de dinainte și asta
    const j = (i - 1 + n) % n;
    const da = dep[j] ?? 0;
    if (da <= 0) continue;
    const v = ring[i], na = norm[j];
    bucati.push([inchis([[v[0], v[1]],
                         [v[0] + na[0] * da, v[1] + na[1] * da],
                         [v[0] + na[0] * da + q[0] * d,
                          v[1] + na[1] * da + q[1] * d],
                         [v[0] + q[0] * d, v[1] + q[1] * d]])]);
  }
  if (bucati.length === 1) return arieBruta(ring);
  const geom = bucati as unknown as PoligonTaiat[];
  const tot = taie().union(geom[0], ...geom.slice(1));
  let arie = 0;
  for (const poligon of tot) {
    poligon.forEach((inel, k) => {
      // primul inel e conturul, celelalte sunt goluri
      arie += (k === 0 ? 1 : -1) * arieBruta(inel as unknown as Pt[]);
    });
  }
  return arie;
}

/** Normala laturii `i`, îndreptată ÎN AFARA inelului. Se alege probând: un pas
 *  mic pe ea trebuie să iasă din cameră. Așa nu contează cum e înfășurat
 *  inelul — iar inelele astea vin și din import, și din editor. */
function normalaInAfara(ring: readonly Pt[], i: number): Pt {
  const a = ring[i], b = ring[(i + 1) % ring.length];
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const L = Math.hypot(dx, dy) || 1;
  const n: Pt = [-dy / L, dx / L];
  const m: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const spre: Pt = [m[0] + n[0] * PAS_SONDA, m[1] + n[1] * PAS_SONDA];
  return inInel(spre, ring) ? [-n[0], -n[1]] : n;
}

/** INELUL, CU LATURILE ÎNTREGI: vârfurile care nu cotesc se scot.
 *
 *  Inelele camerelor vin pline de puncte puse pe mijlocul unei laturi — un hol
 *  al lui M5 are șase vârfuri pentru un dreptunghi. Ele nu strică aria utilă
 *  (formula lui Gauss trece peste ele), dar RUP LATURA ÎN BUCĂȚI, iar fiecare
 *  bucată își caută zidul singură. Când una dintre ele cade tocmai într-un GOL
 *  DE UȘĂ, acolo nu e niciun zid de găsit, și bucata aia rămâne nesocotită —
 *  deși zidul trece pe deasupra ușii și e tot al camerei. Măsurat pe M5: un
 *  dormitor ieșea 19,40 m² construiți în loc de 20,21.
 *
 *  (Curățarea a fost scoasă o dată, fiindcă nicio probă nu se schimba fără
 *  ea — și a trebuit pusă la loc: deosebirea se vedea numai pe planșa
 *  desenată. De-aia are acum proba ei.)
 *
 *  Pragul e de o jumătate de milimetru de abatere de la dreapta vecinilor: sub
 *  atât, vârful nu desparte două laturi, e un punct pus pe una. */
function curataInelul(ring: readonly Pt[]): Pt[] {
  const fara: Pt[] = [];
  for (const q of ring) {
    const u = fara[fara.length - 1];
    if (!u || Math.hypot(q[0] - u[0], q[1] - u[1]) > 1e-4) fara.push([q[0], q[1]]);
  }
  while (fara.length > 1) {
    const a = fara[0], b = fara[fara.length - 1];
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) > 1e-4) break;
    fara.pop();
  }
  const out: Pt[] = [];
  for (let i = 0; i < fara.length; i++) {
    const a = fara[(i - 1 + fara.length) % fara.length];
    const b = fara[i];
    const c = fara[(i + 1) % fara.length];
    const ux = b[0] - a[0], uy = b[1] - a[1];
    const vx = c[0] - b[0], vy = c[1] - b[1];
    const lu = Math.hypot(ux, uy), lv = Math.hypot(vx, vy);
    if (lu < 1e-9 || lv < 1e-9) continue;
    const abate = Math.abs(ux * vy - uy * vx) / Math.max(lu, lv);
    if (abate > 5e-4) out.push(b);
  }
  return out.length >= 3 ? out : fara;
}

/** Aria construită a unei camere — v. nota de deasupra. */
export function arieConstruita(
  ring0: readonly Pt[], pereti: readonly { inel: Pt[] }[],
  celelalte: readonly (readonly Pt[])[],
): number {
  const ring = curataInelul(ring0);
  if (ring.length < 3) return 0;
  const dep = ring.map((_, i) => {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 1e-6) return 0;
    const n = normalaInAfara(ring, i);
    const gasite: number[] = [];
    for (let s = 1; s <= SONDE_PE_LATURA; s++) {
      const t = s / (SONDE_PE_LATURA + 1);
      const m: Pt = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      const z = zidulDeDincolo(m, n, pereti, celelalte);
      if (z) gasite.push(z.intreCamere ? z.gros / 2 : z.gros);
    }
    if (!gasite.length) return 0;
    gasite.sort((x, y) => x - y);
    return gasite[gasite.length >> 1];
  });
  return Math.round(regiuneaCladita(ring, dep) * 100) / 100;
}

export type Camera = {
  kind: string;
  /** Numele scris pe ea, dacă nu se cheamă ca felul ei. V. `CameraDoc.label`. */
  eticheta?: string;
  side: "A" | "B";
  flat?: number;
  /** Aria DESENATĂ acum — se schimbă odată cu pereții. */
  arie: number | null;
  /** Aria scrisă în document, dacă e scrisă. Poate rămâne în urmă până la
   *  salvare, când serverul o rescrie. */
  arieScrisa: number | null;
  /** ARIA CONSTRUITĂ — v. `arieConstruita`. Pe o cameră fără niciun zid în
   *  jur (un balcon desenat liber) iese egală cu cea utilă. */
  arieCladita: number | null;
  inel: Pt[];
  /** Centrul vârfurilor — unde stă eticheta. */
  centru: Pt;
  /** Latura mică a cadrului de încadrare: sub un prag, eticheta nu încape. */
  mic: number;
  /** Finisajele, cu implicitul felului deja aplicat — aici nu mai există
   *  «nespus», fiindcă tot ce desenează are nevoie de un răspuns. Ce a ALES
   *  cineva se citește din `plan.finishes`; panoul are nevoie de distincția
   *  asta, desenul nu. */
  pardoseala: string;
  perete: string;
  /** SPAȚIU EXTERIOR, și care: `balcony` = dală în consolă, în afara benzii;
   *  `loggia` = scobitură în volum. `null` = o cameră ca oricare.
   *
   *  Aici nu mai există «nespus»: un balcon transcris fără `outdoor_kind` își
   *  primește felul din geometrie — în afara anvelopei e consolă, înăuntru e
   *  logie —, fiindcă tot ce desenează și tot ce socotește are nevoie de un
   *  răspuns. Ce a SCRIS cineva bate deducția, ca peste tot. */
  afara: "balcony" | "loggia" | null;
};

export type Perete = {
  id: string;
  kind: FelPerete | string;
  material: string;
  grosime: number;
  /** Înălțimea REALĂ, cu implicitul deja aplicat: aici nu mai există `null`,
   *  fiindcă tot ce desenează are nevoie de un număr. */
  inaltime: number;
  /** Înălțimea DECLARATĂ de perete, sau `null` când merge până la placă. Cele
   *  două nu se pot topi într-una: panoul trebuie să poată arăta diferența
   *  dintre «2,80 fiindcă atâta e nivelul» și «2,80 fiindcă așa scrie», iar un
   *  perete care urmează nivelul trebuie să-l urmeze și după ce se schimbă. */
  proprie: number | null;
  /** Lungimea pe axa lungă a inelului — ce arată panoul ca «Lungime». */
  lungime: number;
  /** Sub tăietura planului (1,15 m): se desenează ca ceva peste care privești,
   *  nu ca ceva tăiat. Un parapet e cazul. */
  subTaietura: boolean;
  /** Rangul de desen scris de mână — v. `PereteDoc.rang`. Lipsă = ordinea din
   *  document, ca până acum. */
  rang?: number;
  /** Curbura, în convenția DXF. 0 = drept. */
  bulge: number;
  /** Axul, când peretele e curb — capetele arcului. */
  ax: [Pt, Pt] | null;
  inel: Pt[];
  peFatada: boolean;
  inchideNucleu: boolean;
  /** Straturile, de la stânga la dreapta. Lista goală = zid omogen — desenul
   *  îl hașurează atunci după rol, ca până acum. */
  straturi: Strat[];
};

/** Un strat, ca îl citește desenul. Aceleași două câmpuri ca în document:
 *  nimic de derivat, dar tipul e al VEDERII, ca panoul și desenul să nu umble
 *  în document. */
export type Strat = { kind: string; grosime: number };

/** MODELELE DE UȘĂ pe care le desenează planșa, cu numele din schema
 *  catalogului (`plan.doors[].model`). Trei, fiindcă atâtea au semne diferite
 *  în convenția de plan: o foaie cu balama și arcul ei, două foi care se
 *  întâlnesc la mijloc, o foaie care alunecă de-a lungul zidului. Prima e
 *  implicita: toate cele 73 de uși ale catalogului sunt uși simple. */
export const MODELE_USA = ["single", "double", "sliding"] as const;
export type ModelUsa = (typeof MODELE_USA)[number];

export function modelUsa(model: string | undefined): ModelUsa {
  return (MODELE_USA as readonly string[]).includes(model ?? "")
    ? (model as ModelUsa) : "single";
}

/** LĂȚIMEA TOCULUI, implicită pe rol: 6 cm un toc obișnuit de lemn sau de
 *  metal cu căptușeala lui, 8 cm tocul unei uși de intrare, care e o ușă
 *  grea, cu prag și cu blindaj. Sunt implicitele pe care le scrie editorul în
 *  document când pune o ușă; desenul citește doar `frame_m`. */
export const TOC_IMPLICIT = 0.06;
export const TOC_INTRARE = 0.08;

/** GOLUL IMPLICIT, când documentul nu-l scrie: 0,90 × 2,10 m, ușa de serie.
 *  Erau scrise de câte trei ori, în fiecare panou care le arăta. */
export const LATIME_GOL = 0.9;
export const INALTIME_GOL = 2.1;

/** LUMINA CEA MAI MICĂ pe care o poate avea un gol, în metri. Nu e o normă —
 *  e capătul de jos al cifrelor care mai înseamnă ceva: sub 30 cm nu mai e o
 *  ușă, e o crăpătură, iar câmpurile legate între ele (v. `luminaGolului`) au
 *  nevoie de un capăt ca să nu se împingă unul pe altul în negativ. */
export const LUMINA_MIN = 0.3;

/** LUMINA GOLULUI — cât rămâne liber între tocuri, adică pe unde chiar se
 *  trece. Golul din zid e lumina plus cele două tocuri, aceeași socoteală pe
 *  care o face și semnul de pe planșă (v. `Desen::simbolUsa`). */
export function luminaGolului(latime: number, toc: number): number {
  return Math.max(latime - 2 * toc, 0);
}

/** Golul din zid cerut de o lumină dată, cu tocurile ei. Inversa celeilalte. */
export function golulPentruLumina(lumina: number, toc: number): number {
  return lumina + 2 * toc;
}

/** Tocul în care stă balamaua — v. `GolDoc.hinge`. */
export type Balama = "start" | "end";

export type Gol = {
  fel: "usa" | "fereastra";
  /** Modelul, pentru uși — v. `MODELE_USA`. Ferestrele îl au «single». */
  model: ModelUsa;
  /** UȘA DE INTRARE ÎN LOCUINȚĂ: cea dintre coridorul comun al tronsonului
   *  («hol») și holul sau livingul apartamentului. Se desenează mai apăsat
   *  decât celelalte, fiindcă pe planșă ea spune unde începe locuința — v.
   *  `esteIntrare`. */
  intrare: boolean;
  /** Lățimea tocului, în metri — din document, altfel implicitul rolului. */
  toc: number;
  /** Tocul balamalei: `start` = `a`, `end` = `b`. */
  balama: Balama;
  /** Poziția în lista ei din document (`plan.doors` sau `plan.windows`) — prin
   *  ea se scrie înapoi când i se schimbă sensul. */
  idx: number;
  /** Cele două tocuri ale golului. */
  a: Pt;
  b: Pt;
  c: Pt;
  /** Lățimea GOLULUI din zid, de la toc la toc — cota care se scrie pe planșă.
   *  Lumina, adică pe unde se trece, e ea minus tocuri (v. `luminaGolului`). */
  latime: number;
  /** Înălțimea golului, în metri — din document, altfel `INALTIME_GOL`. */
  inaltime: number;
  parapet: number | null;
  /** Peretele-gazdă. */
  perete: string | null;
  /** Spre ce cameră se deschide ușa — mijlocul camerei dinspre care se
   *  balansează foaia. `null` când nu s-a putut afla (v. `spreCamera`). */
  spre: Pt | null;
};

/** Un aparat electric, gata de desenat: cu înălțimea și circuitul lui aflate. */
export type Electric = {
  /** Poziția în `plan.electrical` — prin ea se scrie înapoi. */
  idx: number;
  kind: string;
  c: Pt;
  /** Înălțimea de montaj, de la pardoseala finită. */
  h: number;
  /** Rotirea, în grade: încotro «privește» aparatul, adică dinspre ce perete. */
  deg: number;
  circuit: string;
  /** Piesa care-l justifică și partea ei — v. `ElectricDoc::pentru`. */
  pentru: number | null;
  parte: "stanga" | "dreapta" | "spate" | null;
  /** Oglindirea simbolului, pe axa lui lungă și pe cea scurtă — v.
   *  `ElectricDoc::flip_x`. */
  oglX: boolean;
  oglY: boolean;
  /** A box's devices; absent on a single device. */
  devices?: string[];
  /** The circuits a box's routes take, one route each; absent on a single
   *  device, whose route takes `circuit`. */
  circuite?: string[];
};

/** O COTĂ PUSĂ DE MÂNĂ, de la un punct la altul.
 *
 *  Cotele planșei se SOCOTESC din geometrie, la fiecare desenare (v.
 *  `cote::cotelePlanului`): ele măsoară ce e desenat, deci nu stau în document
 *  și nu se pot șterge — se sting doar odată cu zidul pe care îl măsoară. Dar
 *  arhitectul are nevoie și de măsuri pe care planșa nu le scoate singură:
 *  «meniu de cote — să poți să pui de la un punct la celălalt și să-ți adauge
 *  cota» (15.10.2026). Alea se scriu aici și se desenează alături de celelalte. */
export type CotaDoc = { a: Pt; b: Pt };

export type PlanTronson = {
  /** Conturul modulului, la front nominal. */
  anvelopa: Pt[];
  camere: Camera[];
  pereti: Perete[];
  goluri: Gol[];
  /** JUMĂTĂȚILE DE UȘĂ, pe camere — v. `Prag`. Intră în aria fiecărei camere
   *  și se desenează cu pardoseala ei; de-aia stau în plan, o dată, și nu se
   *  socotesc a doua oară în desen. */
  praguri: Prag[];
  /** GLAFURILE FERESTRELOR — aceeași bucată de podea în golul unei ferestre.
   *  Intră în arie și în aprindere, ca pragurile (v.
   *  `pervazurileFerestrelor`). */
  pervazuri: Prag[];
  /** Mobilierul pus de mână. Cel dedus din felul camerei nu trece pe-aici. */
  piese: { kind: string; c: Pt; w: number; h: number; deg: number; z: number;
           /** Rampele scării DESENATE (v. `PiesaDoc.scara`). Lipsă = scară pusă
            *  ca o cutie, sau adusă din import. */
           scara?: { rampe: { de: [number, number]; la: [number, number] }[];
                     latime_m: number; treapta_m?: number; pas_m?: number;
                     podest_m?: number;
                     /** Formele podestelor, când nu sunt cele deduse din
                      *  rampe — v. `PiesaDoc.scara.podeste`. */
                     podeste?: { k: number; contur: [number, number][] }[] };
           /** A TREIA MĂSURĂ. Din document când e scrisă, altfel înălțimea de
            *  catalog a felului (v. `FelPiesa.inaltime`). */
           inaltime: number;
           oglX: boolean; oglY: boolean;
           /** ANSAMBLUL din care face parte piesa — v. `PiesaDoc.grup`. */
           grup?: string;
           /** Rangul de desen scris de mână — v. `PiesaDoc.rang`. */
           rang?: number }[];
  /** Prizele, întrerupătoarele și corpurile de iluminat. */
  electrice: Electric[];
  nucleu: { rect: [number, number, number, number];
            parti: { kind: string; inel: Pt[] }[] } | null;
  /** Cadrul de încadrare al anvelopei — pentru `viewBox`. */
  cadru: { x: number; y: number; w: number; h: number };
  colt: boolean;
  /** Înălțimea liberă a nivelului: implicitul oricărui perete care nu-și
   *  declară alta, și plafonul până la care unul poate fi urcat. */
  nivel: number;
  /** COTELE PUSE DE MÂNĂ — v. `CotaDoc`. */
  coteDeMana: CotaDoc[];
  /** Cheile cotelor socotite care au fost STINSE — v. `cote::cheiaCotei`. */
  coteStinse: string[];
};

import { pardoseala as pardoselaImplicita, pereteleCamerei }
  from "./design/finisaje";
import {
  inaltimeaAparatului, circuitulAparatului, circuiteleDozei, inaltimeaDozei,
} from "./design/electric";
// Numai catalogul de feluri — înălțimea obișnuită a piesei. `plan/arhMobilierPlan`
// nu importă din `doc`, deci nu se încheie niciun cerc.
import { felulPiesei } from "../plan/arhMobilierPlan";
import { IFC } from "../generated/kinds";
import { taie } from "../taietor";
import type { Polygon as PoligonTaiat } from "polygon-clipping";

/** E un fel de cameră care stă AFARĂ? Din catalogul canonic, nu dintr-o listă
 *  scrisă aici: `balcony`, `terasa` și `terasa_circulabila` sunt marcate toate
 *  `EXTERNAL` în `generated/kinds::IFC`, și tot acolo intră un fel nou. */
const eAfara = (kind: string): boolean => IFC[kind]?.[1] === "EXTERNAL";

/** ÎNĂLȚIMEA DE NIVEL PRESUPUSĂ când modulul nu o declară — perechea lui
 *  `layout/tronsoane.py::STOREY_H`, și trebuie să rămână egală cu ea: dacă
 *  desenul ar presupune altceva decât validatorul, un perete acceptat acolo ar
 *  apărea aici trecut prin placă. */
export const H_NIVEL = 2.8;

/** Cât are un parapet care nu-și spune înălțimea.
 *
 *  Planșa nu cotează parapeții, deci transcrierea le scrie geometria fără
 *  `height_m` — corect, un document nu inventează măsurători. Dar implicitul de
 *  perete i-ar duce la placă, iar balconul de care țin ar deveni cameră închisă,
 *  și în desen și în machetă. Așa că implicitul stă aici, ca în
 *  `layout/tronsoane.py::PARAPET_H`: 1,00 m, minimul pe care norma îl cere unui
 *  parapet de balcon. Un perete care își declară înălțimea o păstrează. */
export const H_PARAPET = 1.0;

/** Înălțimea unei piese care nu-și declară una și al cărei fel nu are nici el.
 *  0,85 — blatul de lucru, măsura cea mai des întâlnită în mobilier. */
export const INALTIME_PIESA = 0.85;

/** Cota la care se taie planul. Aceeași ca `arh3d.TAIERE`, și din același
 *  motiv: un plan de arhitectură e o secțiune orizontală pe la 1,15 m —
 *  deasupra pervazurilor, sub buiandrugi. Ce e mai scund nu se taie, se
 *  privește de sus, deci se desenează altfel. */
export const TAIERE = 1.15;

/** Lungimea unui perete: a AXULUI, când e scris; altfel latura mare a
 *  dreptunghiului lui de încadrare.
 *
 *  Cadrul singur e o măsură bună doar pe un zid așezat pe axele planșei: pe
 *  unul OBLIC, latura cadrului e proiecția lui, deci mai scurtă decât el. S-a
 *  văzut de îndată ce zidul a căpătat bilă de rotire — un perete de 3,50
 *  întors cu 28° se raporta ca 3,09, și nimeni nu-i tăiase nimic.
 *
 *  Axul e scris de editor pe orice zid pe care l-a atins (`cuAxeScrise`,
 *  `mutaCapatUnic`), deci pe cel desenat aici e mereu acolo; pe unul transcris
 *  și neatins rămâne cadrul, care pentru un zid ortogonal e exact același
 *  număr. Pe un perete în L (rar, dar există în catalog) cifra e tot a
 *  cadrului, nu a desfășuratei. */
function lungimea(ring: readonly Pt[], axa?: readonly Pt[], bulge = 0): number {
  if (axa?.length === 2) {
    const coarda = Math.hypot(axa[1][0] - axa[0][0], axa[1][1] - axa[0][1]);
    if (!bulge) return coarda;
    // pe un perete curb, lungimea e a ARCULUI, nu a coardei: θ = 4·atan(bulge),
    // R = c / (2·sin(θ/2)), L = R·θ — la semicerc, π/2 ≈ 1,57 × coarda
    const teta = 4 * Math.atan(Math.abs(bulge));
    return coarda * teta / (2 * Math.sin(teta / 2));
  }
  const c = cadrul(ring as Pt[]);
  return Math.max(c.w, c.h);
}

/** `a + k·delta`, cu delta = 0. Vezi nota de sus: nominalul e cadrul pe care
 *  catalogul își verifică acoperirea. */
const val = (z: Afin | number): number =>
  typeof z === "number" ? z : z.a;

const inel = (poly: VarfAfin[]): Pt[] =>
  poly.map((q) => [val(q[0]), val(q[1])] as Pt);

function cadrul(pts: Pt[]) {
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** Câte segmente ia un arc. 24 pe un semicerc, proporțional mai puține pe unul
 *  mai mic: la grosimea unui zid și la mărirea la care se citește un plan, un
 *  segment de sub 4° e sub un pixel. Mai multe ar umfla documentul (fiecare
 *  vârf e o pereche afină) fără să se vadă. */
const SEGMENTE = 24;

const r3g = (v: number) => Math.round(v * 1000) / 1000;

/** Cercul pe care stă arcul `a → b` cu `bulge` (convenția din `arcDinAx`):
 *  centrul, raza, unghiul capătului `a` și unghiul la centru, cu semn — pozitiv
 *  când unghiul crește de la `a` la `b`. `null` pe un perete drept.
 *
 *  Vârful arcului e la săgeata `s = |bulge|·c/2` de mijlocul coardei, spre
 *  `sgn · (uy, −ux)`, iar centrul la `R` înapoi de vârf: sub semicerc (s < R)
 *  centrul cade de CEALALTĂ parte a coardei, peste semicerc de aceeași parte.
 *  (Până pe 23.09.2026 centrul stătea mereu de partea vârfului, deci orice arc
 *  sub semicerc se termina alături de capătul lui.) */
export function cerculArcului(a: Pt, b: Pt, bulge: number):
    { cx: number; cy: number; R: number; a0: number; theta: number } | null {
  const [ax, ay] = a;
  const [bx, by] = b;
  const c = Math.hypot(bx - ax, by - ay);
  if (!(c > 1e-9) || !(Math.abs(bulge) >= 1e-9)) return null;
  const theta = 4 * Math.atan(bulge);
  const R = c / (2 * Math.sin(Math.abs(theta) / 2));
  const ux = (bx - ax) / c;
  const uy = (by - ay) / c;
  const sgn = theta > 0 ? 1 : -1;
  const d = (Math.abs(bulge) * c) / 2 - R;
  const cx = (ax + bx) / 2 + uy * d * sgn;
  const cy = (ay + by) / 2 - ux * d * sgn;
  return { cx, cy, R, a0: Math.atan2(ay - cy, ax - cx), theta };
}

/** UN PUNCT PE AXUL UNUI PERETE, drept sau curb: cât de departe e de-a lungul
 *  axului de la capătul `a` (`u`, în metri — pe un arc, lungimea ARCULUI),
 *  piciorul lui pe ax și direcția axului acolo (`deg`, sensul `a → b`).
 *  Punctul se proiectează pe dreaptă, respectiv radial pe arc; `u` se ține
 *  între capete. E convenția lui `u_m` din ambele formate (v.
 *  `canonical/docs/json-format.md`, «Pereții curbi»). */
export function pePeretele(a: Pt, b: Pt, bulge: number, p: Pt):
    { u: number; punct: Pt; deg: number } {
  const k = cerculArcului(a, b, bulge);
  if (!k) {
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L;
    const u = Math.min(L, Math.max(0, (p[0] - a[0]) * ux + (p[1] - a[1]) * uy));
    return { u, punct: [a[0] + ux * u, a[1] + uy * u],
             deg: (Math.atan2(uy, ux) * 180) / Math.PI };
  }
  const { cx, cy, R, a0, theta } = k;
  const sgn = theta > 0 ? 1 : -1;
  const tot = Math.abs(theta);
  const DOI_PI = 2 * Math.PI;
  // cât s-a rotit de la `a`, în sensul arcului, în [0, 2π)
  let t = (sgn * (Math.atan2(p[1] - cy, p[0] - cx) - a0)) % DOI_PI;
  if (t < 0) t += DOI_PI;
  // în afara arcului: la capătul cel mai apropiat
  if (t > tot) t = t - tot < DOI_PI - t ? tot : 0;
  const unghi = a0 + sgn * t;
  return {
    u: R * t,
    punct: [cx + R * Math.cos(unghi), cy + R * Math.sin(unghi)],
    deg: (Math.atan2(sgn * Math.cos(unghi), -sgn * Math.sin(unghi)) * 180) / Math.PI,
  };
}

/** ARCUL UNUI ZID, ca listă de puncte: din capetele axului și `bulge`
 *  (convenția DXF, `tan(θ/4)`, unde θ e unghiul la centru).
 *
 *  SEMNUL LUI `bulge` e al DXF-ului, citit în coordonatele DOCUMENTULUI:
 *  `bulge > 0` = arcul merge de la capătul 0 la capătul 1 în sensul în care
 *  unghiul CREȘTE (de la +x spre +y). Vârful arcului e deci la mijlocul
 *  coardei plus `bulge · (dy, −dx) / 2`. Pe desenul editorului, unde y crește
 *  în jos, sensul ăsta se vede ca al acelor de ceasornic; o oglindire îl
 *  întoarce, deci schimbă semnul.
 *
 *  `offset` mută arcul PARALEL cu el însuși — radial pe unul curb, pe normală
 *  pe unul drept. Cu el se obțin fețele unui zid (±t/2) și, de când zidurile au
 *  straturi, fețele fiecărui STRAT: aceeași linie, la alte două depărtări.
 *
 *  SEMNUL LUI `offset`: `> 0` duce spre `(-dy, dx)` față de direcția capătului
 *  0 → capătul 1, pe drept și pe curb deopotrivă. Pe desen, unde y crește în
 *  JOS, direcția aia e mâna DREAPTĂ a celui care merge de la primul capăt spre
 *  al doilea — v. `STANGA` din `design/straturi.ts`, singurul loc unde stânga
 *  și dreapta se numesc.
 *
 *  STĂ AICI, ÎN DOCUMENT, și nu în editor, fiindcă o citesc amândoi: editorul
 *  ca să refacă poligonul unui zid mutat, desenul ca să hașureze straturile. A
 *  doua copie a ei ar fi însemnat două forme pentru același zid curb. */
export function arcDinAx(a: Pt, b: Pt, bulge: number, offset = 0): Pt[] {
  const [ax, ay] = a;
  const [bx, by] = b;
  const c = Math.hypot(bx - ax, by - ay);
  if (!(c > 1e-9) || Math.abs(bulge) < 1e-9) {
    // Drept: offsetul e o simplă deplasare pe normală.
    const nx = -(by - ay) / (c || 1);
    const ny = (bx - ax) / (c || 1);
    return [[ax + nx * offset, ay + ny * offset],
            [bx + nx * offset, by + ny * offset]];
  }
  const { cx, cy, R, a0, theta } = cerculArcului(a, b, bulge)!;
  const sgn = theta > 0 ? 1 : -1;
  const a1 = a0 + theta;
  // spre `(−dy, dx)` = spre interiorul unui arc cu `bulge > 0`
  const Rr = Math.max(R - offset * sgn, 0.001);
  const n = Math.max(2, Math.ceil((Math.abs(theta) / Math.PI) * SEGMENTE));
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const t = a0 + ((a1 - a0) * i) / n;
    out.push([r3g(cx + Math.cos(t) * Rr), r3g(cy + Math.sin(t) * Rr)]);
  }
  return out;
}

/** Punctul e în inel? Aruncare de rază pe orizontală, cu numărare de
 *  traversări — la fel ca `arh3d.cameraDinPunct`, și de-ajuns: inelele
 *  camerelor nu se suprapun, iar un modul are cel mult 21. */
export function inInel(p: Pt, ring: readonly Pt[]): boolean {
  let inauntru = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > p[1] !== yj > p[1]
        && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
      inauntru = !inauntru;
    }
  }
  return inauntru;
}

/** ANVELOPA MODULULUI, în cele două cadre.
 *
 *  Repetă contururile din `layout/tronsoane.py::_check_walls` — și trebuie să
 *  le repete: acolo se dovedește că pereții plus camerele acoperă cel puțin
 *  90% din ANVELOPA ASTA. Un desen pe alt contur ar arăta goluri unde proba
 *  spune că nu sunt, sau invers.
 */
export function anvelopa(doc: TronsonDoc): Pt[] {
  const plan = doc.plan;
  // CONTURUL SCRIS BATE CIFRELE. Odată ce cineva a tras de o latură, forma e
  // poligonul — nu se mai poate deduce, fiindcă două numere nu descriu șapte
  // laturi. V. `plan.outline`.
  if (plan?.outline && plan.outline.length >= 3) {
    return plan.outline.map((q) => [q[0], q[1]] as Pt);
  }
  if (doc.role.startsWith("corner") && plan?.measured_leg_a_m != null) {
    const L = plan.measured_leg_a_m;
    const n = plan.notch_m ?? 0;
    return [[0, 0], [L, 0], [L, L - n], [L - n, L - n], [L - n, L], [0, L]];
  }
  const F = doc.front.nominal_m;
  // IZOLATUL E UN DREPTUNGHI CU ORIGINEA ÎN COLȚ, ca al colțului dar fără
  // crestătură: n-are coridor, deci n-are axă pe care să stea y = 0.
  if (doc.role === "standalone") {
    return [[0, 0], [F, 0], [F, doc.band_depth_m], [0, doc.band_depth_m]];
  }
  const h = doc.band_depth_m / 2;
  return [[0, -h], [F, -h], [F, h], [0, h]];
}

/** Cele două tocuri ale unui gol, din centru + lățime + rotație.
 *
 *  `rot_deg` e direcția golului, nu a foii: 0 = de-a lungul lui x, 90 = de-a
 *  lungul lui y. În catalogul de azi nu apar decât astea două, dar unghiul se
 *  folosește ca atare, ca un modul rotit să nu ceară o a doua ramură. */
function tocuri(g: GolDoc): { a: Pt; b: Pt; c: Pt; latime: number } {
  const c: Pt = [val(g.x), val(g.y)];
  const w = g.w_m ?? LATIME_GOL;
  const rad = ((g.rot_deg ?? 0) * Math.PI) / 180;
  const dx = (Math.cos(rad) * w) / 2;
  const dy = (Math.sin(rad) * w) / 2;
  return { a: [c[0] - dx, c[1] - dy], b: [c[0] + dx, c[1] + dy], c, latime: w };
}

/** Punctul spre care se deschide o ușă cu sensul SCRIS: la o jumătate de metru
 *  de gol, pe normala lui, în partea dată de semn. Aceeași formă ca ce întoarce
 *  `spreCamera`, ca desenul să nu aibă două căi de citit sensul. */
function partea(t: { a: Pt; b: Pt; c: Pt }, swing: 1 | -1): Pt {
  const ux = t.b[0] - t.a[0];
  const uy = t.b[1] - t.a[1];
  const L = Math.hypot(ux, uy) || 1;
  return [t.c[0] + (-uy / L) * 0.5 * swing, t.c[1] + (ux / L) * 0.5 * swing];
}

/** CORIDORUL COMUN al tronsonului: camera de fel «hol» (HOL DISTRIBUȚIE pe
 *  planșă), din care se intră în locuințe. Holul locuinței e «hall». */
const CORIDOR = "hol";

/** Camerele de CIRCULAȚIE: coridorul comun al tronsonului și holul locuinței.
 *  O ușă nu se deschide în ele — v. `spreCamera`. */
const CIRCULATIE = new Set(["hol", "hall", "sas"]);

/** SPRE CE CAMERĂ SE DESCHIDE O UȘĂ, dedus — fiindcă documentul n-o spune.
 *
 *  Formatul canonic poartă `hinge` și `tip` pe fiecare ușă, deci
 *  `ApartmentView` desenează baleiajul fără să ghicească. Un document de
 *  tronson n-are așa ceva: are punctul, lățimea și unghiul.
 *
 *  REGULA, în ordinea în care se aplică:
 *
 *    1. **o singură parte e cameră** — acolo se deschide, n-are unde altundeva;
 *    2. **amândouă sunt camere, una e circulație** — se deschide în CEALALTĂ.
 *       Asta nu e o preferință de desen: o foaie care baleiază în coridor îl
 *       blochează, iar pe traseul de evacuare al unui tronson e chiar lucrul
 *       care nu se face. Ușile se deschid din hol ÎN cameră;
 *    3. **amândouă sunt camere obișnuite** — în cea mai mare, ca foaia să aibă
 *       loc. E o convenție de desen, nu o măsurătoare, și de-aia se poate
 *       întoarce din interfață.
 *
 *  Prima variantă se oprea la pasul 1 și întorcea `null` pentru orice ușă cu
 *  camere de amândouă părțile — «un arc pus la întâmplare e o afirmație despre
 *  plan». Adevărat, dar consecința era că NICIO ușă interioară nu primea
 *  baleiaj: măsurat pe M8, zero din zece. Un plan de arhitectură fără baleiaje
 *  nu arată cât spațiu cere o ușă, adică taman ce se verifică pe el.
 */
function spreCamera(t: { a: Pt; b: Pt; c: Pt }, camere: Camera[]): Pt | null {
  const ux = t.b[0] - t.a[0];
  const uy = t.b[1] - t.a[1];
  const L = Math.hypot(ux, uy) || 1;
  const nx = -uy / L;
  const ny = ux / L;
  const D = 0.5;
  const parti = ([-1, 1] as const).map((s) => {
    const p: Pt = [t.c[0] + nx * D * s, t.c[1] + ny * D * s];
    const cam = camere.find((c) => inInel(p, c.inel));
    return cam ? { p, cam } : null;
  });
  const [jos, sus] = parti;
  if (!jos && !sus) return null;
  if (!jos) return sus!.p;
  if (!sus) return jos.p;
  // CORIDORUL COMUN E CEL MAI TARE «NU»: ușa de intrare stă între două
  // circulații — coridorul tronsonului și holul locuinței — și, judecate la
  // fel, foaia cădea în camera mai mare, adică ÎN CORIDOR, pe traseul de
  // evacuare (măsurat pe M2: amândouă intrările baleiau în HOL DISTRIBUȚIE).
  // Se intră din coridor în locuință, deci foaia se deschide în hol.
  const kj = jos.cam.kind === CORIDOR;
  const ks = sus.cam.kind === CORIDOR;
  if (kj !== ks) return kj ? sus.p : jos.p;
  const cj = CIRCULATIE.has(jos.cam.kind);
  const cs = CIRCULATIE.has(sus.cam.kind);
  if (cj !== cs) return cj ? sus.p : jos.p;
  return (jos.cam.arie ?? 0) >= (sus.cam.arie ?? 0) ? jos.p : sus.p;
}

/** PRIN CE SE INTRĂ ÎNTR-O LOCUINȚĂ: prin holul ei (vestibulul) sau, la
 *  planurile fără vestibul, direct în living. Niciodată printr-o baie: pe M1
 *  băile stau lipite de coridor și au ușa transcrisă spre el, iar fără regula
 *  asta ieșeau «intrări». */
const PRIN_CARE_SE_INTRA = new Set(["hall", "living"]);

/** E UȘA ASTA INTRAREA ÎNTR-O LOCUINȚĂ?
 *
 *  ROLUL SCRIS BATE DEDUCȚIA: `kind: "entrance"` spune da, `room` sau `fire`
 *  spun nu. Deducția rămâne pentru documentele care nu-l poartă: da, când de
 *  o parte a ușii e coridorul comun și de cealaltă holul sau livingul unui
 *  apartament. Aceeași sondă la o jumătate de metru pe normală ca în
 *  `spreCamera`, ca cele două citiri ale ușii să nu se contrazică. O ușă cu
 *  coridorul de amândouă părțile, sau fără nicio cameră de partea cealaltă
 *  (spre balcon, spre exterior), nu e intrare. */
function esteIntrare(g: GolDoc, t: { a: Pt; b: Pt; c: Pt },
                     camere: Camera[]): boolean {
  if (g.kind) return g.kind === "entrance";
  const ux = t.b[0] - t.a[0];
  const uy = t.b[1] - t.a[1];
  const L = Math.hypot(ux, uy) || 1;
  const parti = ([-1, 1] as const).map((s) => {
    const p: Pt = [t.c[0] + (-uy / L) * 0.5 * s, t.c[1] + (ux / L) * 0.5 * s];
    return camere.find((c) => inInel(p, c.inel)) ?? null;
  });
  const [jos, sus] = parti;
  if (!jos || !sus) return false;
  const [coridor, camera] = jos.kind === CORIDOR ? [jos, sus] : [sus, jos];
  return coridor.kind === CORIDOR && PRIN_CARE_SE_INTRA.has(camera.kind);
}

/** PARAPEȚII DE STICLĂ — zidurile care închid un balcon spre afară.
 *
 *  Marginea de afară a unui balcon NU e zidărie: e un parapet de sticlă, și
 *  se desenează ca atare — două linii subțiri, fără poché și fără hașură.
 *  Cerut pe 29.09.2026, arătând trei randări: «we should have the lines
 *  from C».
 *
 *  SE RECUNOAȘTE DIN GEOMETRIE, nu din `kind`. Pe modulul venit din imagine
 *  (M9B) marginea balconului de 11,80 m² e făcută din trei pereți de 10 cm
 *  scriși `partition` — importul n-are de unde ști că sunt parapeți.
 *
 *  UN BALCON SE ÎMPREJMUIEȘTE CU STICLĂ, ORICUM AR FI AJUNS ÎN DOCUMENT.
 *  Cerut textual: «if from the solution we have a balcony like in the json we
 *  should put glass railings along the edges. If we extract the balcony from an
 *  image we should also put glass railings and not see them as walls. just line
 *  the balcony with glass railings».
 *
 *  Deci întrebarea e ce are zidul pe FAȚA DINSPRE AFARĂ, nu dacă atinge un
 *  balcon: un zid cu balcon de o parte și CAMERĂ de cealaltă e fațada dinapoia
 *  balconului — zidărie adevărată, și rămâne așa (pe M9B, W7). Orice alt zid
 *  care atinge un spațiu exterior împrejmuiește, deci e sticlă.
 *
 *  ERA «AFARĂ DE-O PARTE ȘI NIMIC DE CEALALTĂ», scos dintr-un singur caz și
 *  prea strâmt. Sonda nu cade întotdeauna în gol dincolo de parapet: pe un
 *  document transcris din imagine, conturul balconului e dus până la muchia de
 *  afară a zidului, nu până la fața lui dinăuntru, așa că sonda se întoarce tot
 *  în balcon — și atunci proba pica, iar marginea balconului rămânea hașurată.
 */
/** CADRUL A TOT CE SE DESENEAZĂ — anvelopa plus orice a ieșit din ea.
 *
 *  `plan.cadru` e cadrul ANVELOPEI și trebuie să rămână așa: pe el se așază
 *  masca hârtiei și socotelile de acoperire (v. `Desen`, nota măștii). Dar
 *  ÎNCADRAREA — „arată-mi tot" — nu poate merge pe el: o piesă trasă afară din
 *  ziduri rămâne desenată, numai că `0`/`Z` n-o mai cuprind, deci nu mai ai cum
 *  să ajungi înapoi la ea. Din afară se citește «mi-a dispărut obiectul»
 *  (26.10.2026: «când mut un obiect în afara planului mi-l ascunde»).
 *
 *  Nimic nu se lărgește degeaba: cu tot ce e desenat ÎN anvelopă, reuniunea e
 *  chiar `plan.cadru`, deci încadrarea obișnuită rămâne neatinsă.
 *
 *  PIESA SE IA CU SEMIDIAGONALA, nu cu laturile: cutia ei e rotită cu `deg`,
 *  iar semidiagonala o cuprinde la orice unghi, fără sinus și cosinus. */
export function cadrulDesenului(plan: {
  cadru: { x: number; y: number; w: number; h: number };
  piese?: readonly { c: Pt; w: number; h: number }[];
  electrice?: readonly { c: Pt }[];
  pereti?: readonly { inel: readonly Pt[] }[];
}): { x: number; y: number; w: number; h: number } {
  const pts: Pt[] = [[plan.cadru.x, plan.cadru.y],
                     [plan.cadru.x + plan.cadru.w, plan.cadru.y + plan.cadru.h]];
  // LISTELE SE CER CU GRIJĂ: un plan derivat le are mereu, dar funcția asta e
  // chemată din `incadreaza`, pe orice plan i se dă — și un cadru care ARUNCĂ
  // ar opri panoramarea cu totul, adică mult mai rău decât ce repară.
  for (const p of plan.piese ?? []) {
    const r = Math.hypot(p.w, p.h) / 2;
    pts.push([p.c[0] - r, p.c[1] - r], [p.c[0] + r, p.c[1] + r]);
  }
  // Aparatele n-au cutie în planul derivat: contează doar să intre în cadru.
  for (const e of plan.electrice ?? []) pts.push([e.c[0], e.c[1]]);
  for (const w of plan.pereti ?? []) pts.push(...w.inel);
  return cadrul(pts);
}

export function parapetiiDeSticla(
  camere: readonly Camera[], pereti: readonly Perete[],
): Set<number> {
  const din = new Set<number>();
  /** Atinge zidul ăsta un spațiu exterior? (cutie pe cutie — destul, fiindcă
   *  întrebarea e «e pe marginea balconului», nu «cât de mult din el»). */
  const langaAfara = (w: Perete) => {
    const c = cadrul(w.inel);
    return camere.some((r) => {
      if (!r.afara) return false;
      const b = cadrul(r.inel);
      return c.x <= b.x + b.w + 0.12 && b.x <= c.x + c.w + 0.12
        && c.y <= b.y + b.h + 0.12 && b.y <= c.y + c.h + 0.12;
    });
  };
  pereti.forEach((w, i) => {
    // CE SCRIE BATE CE SE DEDUCE, ca peste tot. Un zid de fel `parapet` pe
    // marginea unui balcon E împrejmuirea lui: n-are rost să mai fie ghicit
    // din sonde, și nici să depindă de cât de strâns i s-a desenat balconul.
    // Deducția de mai jos rămâne pentru documentele care nu spun nimic — cele
    // transcrise din imagine, unde împrejmuirea vine scrisă «compartimentare».
    if (w.kind === "parapet" && langaAfara(w)) { din.add(i); return; }
    if (w.bulge || w.inel.length < 4) return;
    // axa = latura cea mai lungă a inelului; normala, perpendiculara ei
    let ux = 0, uy = 0, cel = 0;
    for (let k = 0; k < w.inel.length; k++) {
      const a = w.inel[k], b = w.inel[(k + 1) % w.inel.length];
      const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (d > cel) { cel = d; ux = (b[0] - a[0]) / d; uy = (b[1] - a[1]) / d; }
    }
    if (!(cel > 0)) return;
    const cx = w.inel.reduce((t, q) => t + q[0], 0) / w.inel.length;
    const cy = w.inel.reduce((t, q) => t + q[1], 0) / w.inel.length;
    const peste = w.grosime / 2 + 0.08;
    // DIN CAPĂT ÎN CAPĂT, nu dintr-un singur punct de la mijloc.
    //
    // Întrebarea e «are zidul ăsta o cameră pe dinăuntru», iar pe un zid de
    // câțiva metri un singur punct nu poate răspunde: el cade unde cade.
    // Inelul camerei se oprește pe fața zidului, deci oriunde cele două se
    // despart — un prag, un zid care taie, un contur dus ceva mai scurt —
    // sonda nimerește ÎNTRE poligoane și se întoarce goală. Atunci `inauntru`
    // iese 0 și fațada se desenează ca o balustradă de sticlă.
    //
    // De-aici defectul arătat de arhitect (21.10.2026): «când prelungesc un
    // perete, la un moment dat îl transformă în parapet». Tras de un capăt,
    // zidul își plimbă mijlocul — cu jumătate din cât i-a mers capătul —, iar
    // la un pas oarecare mijlocul alunecă de sub cameră. Nimic din ce se vede
    // nu s-a schimbat: camera e tot acolo, lipită de zid pe aproape toată
    // lungimea lui. Pe L4 din planșa importată (fațada de 5,94 m dintre living
    // și balcon) sunt 45 cm de tragere, și din nouă stații de-a lungul
    // zidului două văd în continuare livingul.
    //
    // Deci se merge pe axa lungă, din capăt în capăt. STAȚIILE SE RETRAG din
    // colțuri cu o grosime, fiindcă acolo sonda cade în zidul vecin și n-ar
    // spune nimic despre ăsta. ~0,5 m între ele: sub lățimea oricărei camere,
    // deci niciuna nu încape între două stații.
    //
    // SE IESE LA PRIMA CAMERĂ DINĂUNTRU — zidul e zidărie, orice ar fi mai
    // departe. Pe zidurile dintre camere, adică pe cele mai multe, asta se
    // întâmplă la prima stație: regula se recalculează la fiecare pas al unei
    // trageri, deci nu-și poate permite să măture tot ce n-are nevoie.
    const retras = Math.min(w.grosime, cel / 4);
    const intins = cel - 2 * retras;
    const statii = Math.max(2, Math.round(intins / 0.5));
    let afara = 0, inauntru = 0;
    for (let s = 0; s <= statii && !inauntru; s++) {
      const t = -intins / 2 + (intins * s) / statii;
      const ax = cx + ux * t, ay = cy + uy * t;
      for (const g of [1, -1] as const) {
        const px = ax - uy * g * peste;
        const py = ay + ux * g * peste;
        const c = camere.find((r) => inInel([px, py], r.inel));
        if (!c) continue;
        if (c.afara) afara++; else inauntru++;
      }
    }
    if (afara >= 1 && inauntru === 0) din.add(i);
  });
  return din;
}

/** PRAGUL UNEI CAMERE: jumătatea ei din deschiderea unei uși.
 *
 *  Inelul unei camere se oprește pe FAȚA zidului, deci podeaua din deschidere
 *  nu e a nimănui — nici măsurată, nici desenată. Cerut socotită, textual:
 *  «the room section should include the half part of the doorway … we need
 *  areas to be consistent and accurate, we cannot unaccount for that part».
 *  Pe catalogul de azi sunt între 0,3 % și 21 % din aria unei camere: holul
 *  lui M4, cu patru uși pe 3 m², pierdea o cincime din el.
 *
 *  SE TAIE PE AXUL GOLULUI. Fiecare cameră ia de la fața ei până la ax, deci
 *  cele două praguri umplu împreună toată deschiderea — fără gol și fără
 *  suprapunere, oricum ar sta zidul. Pe unul simetric asta E jumătatea lui.
 *
 *  ADÂNCIMEA SE MĂSOARĂ PÂNĂ LA CAMERĂ, nu din grosimea zidului. Grosimea e a
 *  GOLULUI, iar pe un zid dublat ea poate fi a unei singure foi: luată ca
 *  jumătate, pragul s-ar opri în zidărie și ar rămâne o dungă nici desenată
 *  nici socotită între el și cameră. Camera se caută deci în trepte dincolo de
 *  ax — același motiv pentru care căutarea nu e la o distanță fixă: pe M3 și
 *  M6, un punct la 5 cm de fața zidului cădea tot în zidărie — iar adâncimea
 *  se ia pe rază, până la conturul EI.
 *
 *  UNA SINGURĂ, și de-aia stă aici: din ea iese și aria camerei (`deriva`), și
 *  pardoseala desenată în deschidere (`Desen`). Două socoteli ale aceleiași
 *  jumătăți ar putea să se despartă fără ca nimic să se plângă, iar planșa ar
 *  arăta altceva decât scrie pe ea. Perechea din Python — `tronson_edit._praguri`,
 *  cea care rescrie `area_m2` la salvare — desenează la rândul ei același
 *  dreptunghi; proba `pragulUsii.test.ts` le pune față în față pe tot catalogul.
 */
export type Prag = {
  /** Camera căreia îi revine — indice în `camere`. */
  camera: number;
  /** Golul din care vine — indice în `goluri`. SCRIS, nu dedus din vârfuri:
   *  pragul nu mai pornește din capetele golului de când urmează scobitura
   *  camerei, iar o împerechere pe coordonate s-a rupt tăcut exact atunci —
   *  masca a rămas cât zidul și conturul camerei a trecut iar prin
   *  deschidere. */
  gol: number;
  inel: Pt[];
};

/** Cât de departe de ax se caută camera, în metri. Peste jumătatea oricărui
 *  zid din catalog (cea mai groasă e 0,23) și sub grosimea unei camere, ca
 *  sonda să nu treacă dintr-o dată peste una îngustă. */
const PRAGUL_CAUTA = [0.05, 0.12, 0.2, 0.32, 0.5, 0.7];

/** CÂT POATE IEȘI PRAGUL ÎN AFARA GOLULUI, în metri.
 *
 *  Inelul unei camere e SCOBIT în dreptul fiecărei uși, iar scobitura e
 *  amprenta BLOCULUI de ușă: golul plus tocurile lui. Pe M1 golul are 0,90 și
 *  scobitura 1,00 — câte 5 cm de fiecare parte. Pragul urmează scobitura, ca
 *  să n-o lase cu colțurile nepodite; dar unde camera NU e scobită, latura pe
 *  care o lovește sonda e toată fața ei, iar pragul ar acoperi tot zidul.
 *
 *  Marginea e deci cât cel mai lat toc pe care îl cunoaște schema
 *  (`TOC_INTRARE`): mai mult decât atâta nu poate ieși amprenta unui bloc de
 *  ușă dincolo de golul lui. PERECHE cu `_PRAG_PESTE_GOL` din
 *  `architecture/layout/tronsoane.py`. */
const PRAGUL_PESTE_GOL = TOC_INTRARE;

/** Unde taie raza `o + t·d` inelul: depărtarea ȘI latura tăiată.
 *
 *  Latura contează, nu doar depărtarea: inelul unei camere e SCOBIT în dreptul
 *  fiecărei uși — pe M1 holul se retrage cu 8 cm pe o lățime de 1,00 m acolo
 *  unde golul are 0,90 —, iar latura aia e chiar fundul scobiturii. Din
 *  capetele ei se ia lățimea pragului, ca el să umple scobitura până la colțuri
 *  și să nu lase două pătrățele de hârtie la montanți. */
function razaPanaLaInel(o: Pt, d: Pt, ring: readonly Pt[]):
  { t: number; a: Pt; b: Pt } | null {
  let cel: { t: number; a: Pt; b: Pt } | null = null;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    const det = ex * d[1] - d[0] * ey;
    if (Math.abs(det) < 1e-12) continue;
    const ax = a[0] - o[0];
    const ay = a[1] - o[1];
    const t = (ex * ay - ax * ey) / det;
    const u = (d[0] * ay - d[1] * ax) / det;
    if (t > 1e-9 && u >= -1e-9 && u <= 1 + 1e-9 && (cel === null || t < cel.t)) {
      cel = { t, a, b };
    }
  }
  return cel;
}

/** Pragurile tuturor ușilor, pe camere — v. nota de la `Prag`. */
export function pragurileUsilor(
  camere: readonly { inel: Pt[] }[], goluri: readonly Gol[],
): Prag[] {
  return deschideriPodite(camere, goluri, "usa");
}

/** GLAFURILE FERESTRELOR: aceeași bucată de podea, în golul unei ferestre.
 *
 *  Inelul camerei se oprește pe fața zidului și la fereastră, deci în
 *  deschidere rămânea o dungă albă între pardoseală și semnul ferestrei —
 *  exact defectul reparat la uși pe 28.09, văzut acum lângă geam.
 *
 *  O SINGURĂ PARTE PRIMEȘTE, spre deosebire de ușă: căutarea nu găsește
 *  cameră afară, deci partea dinspre stradă se sare singură (`i < 0`), iar
 *  podeaua se oprește pe axul golului. Semnul ferestrei se desenează peste,
 *  cu liniile lui la ±t și ±t/3 — toate în bucata acoperită acum.
 *
 *  INTRĂ ÎN ARIE ȘI ÎN APRINDERE, ca pragul unei uși (hotărât 01.10.2026).
 *
 *  Până atunci nu intra, iar argumentul era al normei: suprafața utilă se
 *  măsoară între fețele zidurilor, iar glaful e grosime de zid, nu podea pe
 *  care se stă. Cerut altfel — camera să fie TOT ce ține între ziduri, cu
 *  scobitura ferestrei cu tot: aleasă, camera lăsa lângă fiecare geam o pată
 *  stinsă pe care ochiul o citea ca pe o scăpare a desenului, nu ca pe o
 *  convenție de măsurat.
 *
 *  CELE DOUĂ MERG ÎMPREUNĂ. «Ce se aprinde e exact ce se socotește» (v.
 *  `Desen`): o aprindere mai mare decât cifra de pe etichetă ar fi fost o a
 *  doua nepotrivire, pusă în locul celei dintâi. Deci glaful intră în
 *  amândouă — și în `tronson_edit`, care rescrie `area_m2` la salvare, altfel
 *  cifra de pe ecran și cea din document s-ar despărți la prima salvare.
 *
 *  RĂMÂNE O LISTĂ SEPARATĂ fiindcă rămâne o GEOMETRIE separată: se desenează
 *  altfel decât pragul (o singură parte primește) și se taie altfel în mască. */
export function pervazurileFerestrelor(
  camere: readonly { inel: Pt[] }[], goluri: readonly Gol[],
  /** Depărtarea de la axul golului până la fața zidului DINSPRE CAMERĂ, pe
   *  partea cerută (`+1` / `-1` pe normala golului). */
  fata?: (g: Gol, spre: 1 | -1) => number,
): Prag[] {
  return deschideriPodite(camere, goluri, "fereastra", fata);
}

/** Miezul comun al celor două: bucata de podea dintre conturul camerei și axul
 *  unei deschideri, pentru golurile de felul cerut. */
function deschideriPodite(
  camere: readonly { inel: Pt[] }[], goluri: readonly Gol[],
  fel: "usa" | "fereastra",
  /** Unde e fața zidului — numai pentru ferestre, ca peticul să se oprească pe
   *  ea (v. `pervazurileFerestrelor`). */
  fata?: (g: Gol, spre: 1 | -1) => number,
): Prag[] {
  const out: Prag[] = [];
  goluri.forEach((g, gi) => {
    if (g.fel !== fel) return;
    const L = g.latime || Math.hypot(g.b[0] - g.a[0], g.b[1] - g.a[1]);
    if (!(L > 0)) return;
    const ux = (g.b[0] - g.a[0]) / L;
    const uy = (g.b[1] - g.a[1]) / L;
    for (const semn of [1, -1] as const) {
      const d: Pt = [-uy * semn, ux * semn];
      let i = -1;
      let cat = 0;
      for (const peste of PRAGUL_CAUTA) {
        const p: Pt = [g.c[0] + d[0] * peste, g.c[1] + d[1] * peste];
        i = camere.findIndex((c) => inInel(p, c.inel));
        if (i >= 0) { cat = peste; break; }
      }
      // Partea asta chiar n-are cameră: o ușă de intrare dă afară, iar acolo
      // nu e nicio podea de dus până la ax.
      if (i < 0) continue;
      // Axul e DEJA în cameră: ușa nu stă într-un zid al ei, deci n-a rămas
      // nimic afară de socotit.
      if (inInel(g.c, camere[i].inel)) continue;
      const lovit = razaPanaLaInel(g.c, d, camere[i].inel);
      if (lovit === null || !(lovit.t > 1e-6) || lovit.t > cat + 1e-9) continue;
      const t = lovit.t;
      // UNDE ÎNCEPE PETICUL. La ușă, pe AXUL golului: cele două camere își
      // împart deschiderea, jumătate fiecare. La fereastră, pe FAȚA zidului —
      // nu e nimeni dincolo cu care s-o împartă, iar podeaua camerei se oprește
      // acolo unde începe zidul. Dus până la ax, peticul acoperea jumătatea
      // dinăuntru a semnului ferestrei: «not go over the window, just go up to
      // its very edge» (01.10.2026).
      const de = fel === "fereastra"
        ? Math.min(t, fata?.(g, semn) ?? 0)
        : 0;
      if (!(t - de > 1e-6)) continue;
      // LĂȚIMEA E A SCOBITURII, nu a golului — v. `razaPanaLaInel` și
      // `PRAGUL_PESTE_GOL`.
      const pe = (q: Pt) => (q[0] - g.a[0]) * ux + (q[1] - g.a[1]) * uy;
      const t0 = Math.max(Math.min(pe(lovit.a), pe(lovit.b)), -PRAGUL_PESTE_GOL);
      const t1 = Math.min(Math.max(pe(lovit.a), pe(lovit.b)),
                          L + PRAGUL_PESTE_GOL);
      if (!(t1 - t0 > 1e-6)) continue;
      const pa: Pt = [g.a[0] + ux * t0, g.a[1] + uy * t0];
      const pb: Pt = [g.a[0] + ux * t1, g.a[1] + uy * t1];
      out.push({
        camera: i,
        gol: gi,
        inel: [
          [pa[0] + d[0] * de, pa[1] + d[1] * de],
          [pb[0] + d[0] * de, pb[1] + d[1] * de],
          [pb[0] + d[0] * t, pb[1] + d[1] * t],
          [pa[0] + d[0] * t, pa[1] + d[1] * t],
        ],
      });
    }
  });
  return out;
}

/** FEȚELE ZIDULUI PE CARE STĂ UN GOL, ca depărtări cu semn de la axul lui.
 *
 *  Nu `perete.grosime`: o fațadă e desenată din mai multe foi lipite — miez și
 *  termoizolație —, iar golul le traversează pe toate. Semnul ușii pornește de
 *  pe FAȚA zidului, deci cifra asta hotărăște unde începe foaia; luată greșit,
 *  ușa se desenează lângă gaura ei.
 *
 *  PURĂ, ca `simbolUsa` și `parapetiiDeSticla`: din ea iese și desenul, și
 *  ștersătura din mască, iar o probă trebuie s-o poată măsura fără să randeze.
 */
export function feteleGazdei(
  pereti: readonly Perete[], g: Gol,
): { jos: number; sus: number } | null {
  const dupaId = new Map(pereti.map((w) => [w.id, w]));
  const LIPIT = 0.02;                 // cât de aproape e „lipit"

      const gazda = g.perete ? dupaId.get(g.perete) : undefined;
      if (!gazda) return null;
      const L = g.latime || 1;
      const ux = (g.b[0] - g.a[0]) / L, uy = (g.b[1] - g.a[1]) / L;
      const nx = -uy, ny = ux;
      const peN = (q: readonly number[]) =>
        (q[0] - g.c[0]) * nx + (q[1] - g.c[1]) * ny;
      const peU = (q: readonly number[]) =>
        (q[0] - g.c[0]) * ux + (q[1] - g.c[1]) * uy;
      // ZIDUL SE CAUTĂ LÂNGĂ GOL, NU ÎN EL.
      //
      // Golul e o GAURĂ: acolo unde e el, zid nu există. Pe M1 fațada e
      // desenată din bucăți cu rost între ele — fereastra de pe W38 are
      // mijlocul la x 4,697, iar W38 se termină la 4,247 —, deci o măsurătoare
      // luată pe mijlocul golului nu găsește nimic și cade înapoi pe grosimea
      // gazdei. De-aia rămânea cât un singur strat.
      //
      // Se ia, deci, de o parte și de alta a golului, la câțiva centimetri
      // dincolo de tocuri, unde chiar e zid; și se păstrează doar feliile care
      // apar pe AMÂNDOUĂ părțile, ca un perete care se oprește la gol să nu
      // fie numărat ca și cum ar trece prin el.
      const LANGA = 0.03;
      const laturi: [number, number][][] = [[], []];
      for (const w of pereti) {
        if (w.bulge || !w.inel.length) continue;
        const n = w.inel.map(peN), u = w.inel.map(peU);
        const u0 = Math.min(...u), u1 = Math.max(...u);
        const felie: [number, number] = [Math.min(...n), Math.max(...n)];
        // NUMAI ZIDURILE CARE MERG DE-A LUNGUL GOLULUI SUNT FOI DE-ALE LUI.
        //
        // Un zid PERPENDICULAR care se oprește în gazdă — un T, adică orice
        // compartimentare pornită din fațadă — se vede de aici ca o felie
        // groasă cât e el de LUNG: de-a lungul golului măsoară o grosime de
        // zid, pe normală un metru și jumătate. Prins în socoteală, umfla
        // «grosimea gazdei» de opt ori (pe M9B: 0,19 m citiți ca 1,61), iar
        // semnul ușii — care pornește de pe fața zidului — ajungea desenat la
        // 70 cm în cameră, lângă gaura lui: «why is this door still not in
        // place». O foaie de zid e mai lungă decât groasă, pe direcția
        // golului; una care e invers merge de-a curmezișul, deci nu e foaie.
        if (felie[1] - felie[0] > u1 - u0) continue;
        [-(L / 2 + LANGA), L / 2 + LANGA].forEach((q, k) => {
          if (u0 - 1e-6 <= q && q <= u1 + 1e-6) laturi[k].push(felie);
        });
      }
      const seAting = (a2: [number, number], b2: [number, number]) =>
        a2[0] <= b2[1] + LIPIT && b2[0] <= a2[1] + LIPIT;
      const felii = laturi[0].filter((x) => laturi[1].some((y) => seAting(x, y)));
      const gN = gazda.inel.map(peN);
      let jos = Math.min(...gN), sus = Math.max(...gN);
      // Se crește cât timp o felie atinge intervalul de-acum.
      for (let pas = 0; pas < felii.length; pas++) {
        let crescut = false;
        for (const [a2, b2] of felii) {
          if (b2 < jos - LIPIT || a2 > sus + LIPIT) continue;
          if (a2 < jos) { jos = a2; crescut = true; }
          if (b2 > sus) { sus = b2; crescut = true; }
        }
        if (!crescut) break;
      }
      return sus - jos > 1e-6 ? { jos, sus }
        : { jos: -gazda.grosime / 2, sus: gazda.grosime / 2 };
}


/** Grosimea aceluiași zid — fețele lui, scăzute. */
export function grosimeaGazdeiPura(
  pereti: readonly Perete[], g: Gol,
): number {
  const f = feteleGazdei(pereti, g);
  return f ? f.sus - f.jos : GROSIME_NESTIUTA;
}

/** Cât are un gol al cărui zid nu se poate măsura. */
export const GROSIME_NESTIUTA = 0.30;

/** Documentul, ca geometrie desenabilă la front nominal. */
export function deriva(doc: TronsonDoc): PlanTronson {
  const plan = doc.plan;
  const colt = doc.role.startsWith("corner");
  const env = anvelopa(doc);

  // Finisajele, indexate pe cameră: lista din document e scurtă (are în ea
  // doar ce s-a schimbat de mână), iar căutarea liniară în ea pentru fiecare
  // cameră ar fi pătratică degeaba.
  const alese = new Map((plan?.finishes ?? []).map((f) => [f.room, f]));
  const camere: Camera[] = (plan?.rooms ?? []).map((r, i) => {
    const ring = inel(r.poly);
    const c = cadrul(ring);
    const fin = alese.get(i);
    return {
      kind: r.kind,
      eticheta: r.label,
      side: r.side,
      flat: r.flat,
      // ARIA SE SOCOTEȘTE DIN DESEN, NU SE CITEȘTE DIN DOCUMENT.
      //
      // Era `r.area_m2` — cifra SCRISĂ, pusă de server la ultima salvare. Trăgeai
      // un zid, camera se făcea mai mare sub ochi, iar numărul de pe ea rămânea
      // cel de ieri: «aria scrisă nu se modifică automat când se trage de
      // pereți». Ce se vede pe planșă trebuie să descrie planșa.
      //
      // Cifra DECLARATĂ rămâne în document și rămâne a serverului: el o rescrie
      // la salvare, cu aceeași formulă. Diferența dintre ele e de rotunjirea
      // transcrierii — pe catalog, sub un centimetru pătrat.
      //
      // Aici e numai POLIGONUL; pragurile ușilor se adună mai jos, după ce se
      // știu golurile (v. `pragurileUsilor`), și abia atunci se rotunjește.
      arie: arieBruta(ring),
      /** Cât spune DOCUMENTUL că are, dacă spune. Panoul are nevoie de
       *  distincție; desenul, nu. */
      arieScrisa: r.area_m2 ?? null,
      // Socotită mai jos, când se știu toate camerele și toți pereții.
      arieCladita: null,
      inel: ring,
      centru: [c.x + c.w / 2, c.y + c.h / 2],
      mic: Math.min(c.w, c.h),
      pardoseala: fin?.floor ?? pardoselaImplicita(r.kind),
      perete: fin?.wall ?? pereteleCamerei(r.kind),
      // AFARĂ E AFARĂ, oricum i-ar spune planșa. Aici scria `r.kind ===
      // "balcony"`, iar vocabularul canonic are TREI feluri EXTERNAL —
      // `balcony`, `terasa`, `terasa_circulabila` (`generated/kinds.ts::IFC`).
      // O terasă ieșea deci «înăuntru»: parapetul de pe muchia ei se desena
      // hașurat ca zidăria, nu ca sticlă (v. `parapetiiDeSticla`), iar
      // importul chiar scoate terase (`test_terasa_din_afara_zidurilor…`).
      // Lista se citește din catalog, nu se scrie aici: un fel exterior nou
      // trebuie să intre o dată, acolo.
      afara: eAfara(r.kind)
        ? (r.outdoor_kind ?? (inInel([c.x + c.w / 2, c.y + c.h / 2], env)
                              ? "loggia" : "balcony"))
        : null,
    };
  });

  const nivel = doc.storey_height_m ?? H_NIVEL;
  const pereti: Perete[] = (plan?.walls ?? []).map((w) => {
    const ring = inel(w.poly);
    const h = w.height_m ?? null;
    const implicit = w.kind === "parapet" ? H_PARAPET : nivel;
    return {
      id: w.id,
      kind: w.kind,
      material: w.material,
      grosime: w.thickness_m,
      inaltime: h ?? implicit,
      proprie: h,
      lungime: lungimea(ring, w.axis as Pt[] | undefined, w.bulge ?? 0),
      subTaietura: (h ?? implicit) < TAIERE,
      rang: w.rang,
      bulge: w.bulge ?? 0,
      ax: (w.axis as [Pt, Pt] | undefined) ?? null,
      inel: ring,
      peFatada: !!w.on_facade,
      inchideNucleu: !!w.encloses_core,
      straturi: (w.layers ?? []).map((st) => ({
        kind: st.kind, grosime: st.thickness_m })),
    };
  });

  const goluri: Gol[] = [
    ...(plan?.doors ?? []).map((g, idx) => ({ g, idx, fel: "usa" as const })),
    ...(plan?.windows ?? []).map((g, idx) => ({ g, idx, fel: "fereastra" as const })),
  ].map(({ g, idx, fel }) => {
    const t = tocuri(g);
    // SENSUL: cel SCRIS bate deducția. Deducția rămâne implicitul, fiindcă
    // niciunul din cele 19 goluri transcrise nu-l poartă — s-ar fi scris de
    // mână, o dată, la fiecare ușă din catalog.
    const dedus = fel === "usa" ? spreCamera(t, camere) : null;
    const spre = fel === "usa" && g.swing
      ? partea(t, g.swing) : dedus;
    const intrare = fel === "usa" && esteIntrare(g, t, camere);
    return {
      fel,
      model: fel === "usa" ? modelUsa(g.model) : "single",
      intrare,
      toc: g.frame_m ?? (intrare ? TOC_INTRARE : TOC_IMPLICIT),
      balama: g.hinge === "end" ? "end" : "start",
      idx,
      a: t.a,
      b: t.b,
      c: t.c,
      latime: t.latime,
      inaltime: g.h_m ?? INALTIME_GOL,
      parapet: g.parapet_m ?? g.sill_m ?? null,
      perete: g.wall ?? null,
      spre,
    };
  });

  // GOLUL SE AȘAZĂ PE MIJLOCUL ANSAMBLULUI DE ZID, nu pe al foii-gazdă.
  //
  // O fațadă e desenată din mai multe foi lipite — miez plus termoizolație —,
  // iar golul e declarat pe UNA dintre ele; axul lui cade atunci pe mijlocul
  // foii, nu al zidului prin care trece. Tot ce se desenează pornește de la
  // axul ăsta și e simetric față de el — semnul ferestrei, ștersătura din
  // mască, zona de apucat, chenarul de selecție —, așa că pe un zid dublat
  // fereastra ieșea cu câțiva centimetri în cameră și lăsa o fâșie de zid
  // neștearsă pe partea cealaltă: «it overlaps with the window» (01.10.2026).
  //
  // Se mută AICI, o dată, nu în fiecare desen: altfel fiecare loc care
  // socotește ceva de la ax ar fi trebuit să-și aducă aminte de corecție, iar
  // unul uitat ar fi dat exact defectul ăsta din nou. Documentul nu se atinge
  // — poziția scrisă rămâne a lui, iar mutarea golului pe desen scrie tot
  // acolo.
  for (const g of goluri) {
    const f = feteleGazdei(pereti, g);
    if (!f) continue;
    const mij = (f.jos + f.sus) / 2;
    if (Math.abs(mij) < 1e-6) continue;
    // …DAR CEL MULT CÂT O FOAIE DE ZID. Un gol care declară un perete pe care
    // nu stă — se întâmplă în documente scrise de mână, și o are chiar fixtura
    // probelor — ar fi măsurat atunci față de un zid de aiurea: acolo `jos` și
    // `sus` sunt LUNGIMEA lui, nu grosimea, iar golul ar sări cu metri.
    // Corecția adevărată e jumătate din foaia de alături, deci sub grosimea
    // gazdei.
    const gazda = pereti.find((w) => w.id === g.perete);
    if (!gazda || Math.abs(mij) > gazda.grosime) continue;
    const L = g.latime || 1;
    const nx = -(g.b[1] - g.a[1]) / L;
    const ny = (g.b[0] - g.a[0]) / L;
    for (const q of [g.a, g.b, g.c] as Pt[]) {
      q[0] += nx * mij;
      q[1] += ny * mij;
    }
  }

  // PRAGURILE UȘILOR, și ariile care le cuprind — v. `Prag`.
  //
  // Abia aici, fiindcă au nevoie și de camere și de goluri: camera spune până
  // unde se măsoară, golul spune unde. Aria se rotunjește O SINGURĂ DATĂ, pe
  // sumă, ca să iasă aceeași cifră ca a serverului (`tronson_edit._arie` plus
  // `tronsoane.praguri_usilor`), care adună la fel.
  const praguri = pragurileUsilor(camere, goluri);
  // ȘI GLAFURILE FERESTRELOR, de la 01.10.2026 — v. `pervazurileFerestrelor`.
  // UNDE SE OPREȘTE PODEAUA DINTR-UN GOL DE FEREASTRĂ: pe fața zidului dinspre
  // cameră. NU la jumătatea foii-gazdă — o fațadă e desenată din mai multe foi
  // lipite (miez + termoizolație), iar golul le traversează pe toate: măsurată
  // pe gazdă, podeaua pornea din mijlocul ansamblului și ieșea peste semnul
  // ferestrei. V. `feteleGazdei`, care adună foile.
  const fataGazdei = (g: Gol, spre: 1 | -1) => {
    const f = feteleGazdei(pereti, g);
    if (!f) return 0;
    return spre > 0 ? f.sus : -f.jos;
  };
  const pervazuri = pervazurileFerestrelor(camere, goluri, fataGazdei);
  for (const p of [...praguri, ...pervazuri]) {
    camere[p.camera].arie! += arieBruta(p.inel);
  }
  for (const c of camere) c.arie = Math.round(c.arie! * 100) / 100;

  // ARIILE CONSTRUITE, după cele utile: au nevoie de toate camerele (ca să se
  // știe care zid e între două dintre ele) și de toți pereții.
  // ZIDUL SE ÎMPARTE NUMAI CU ALT APARTAMENT, nu cu orice cameră de dincolo.
  //
  // Aria construită e A APARTAMENTULUI: «aria construita e perimetrul
  // apartamentului cu tot cu pereti si acolo unde se intersecteaza cu alt
  // apartament pana la jumatatea peretelui comun (fara balcon)» (20.10.2026),
  // lămurit pe urmă: «it s where it meets another apartment, not room (...)
  // The wall between an apt and hallway is counted whole».
  //
  // Socotit pe ORICE cameră vecină, zidul dintre apartament și holul comun
  // ieșea și el pe jumătate — iar holul nu e al nimănui, deci jumătatea
  // cealaltă nu se adună nicăieri și apartamentul ieșea mai mic decât e. Între
  // două camere ALE ACELUIAȘI apartament împărțirea rămâne: cele două jumătăți
  // se adună la loc în suma lui, deci nu se pierde nimic.
  //
  // Camerele fără apartament (holul de distribuție, casa scării) ies din
  // listă: pentru zidul care dă în ele nu mai e nimeni de împărțit, deci se ia
  // ÎNTREG, până la fața lui — ca unul care dă afară.
  const inele = camere.map((c) => c.inel);
  const aleUnuiApartament = camere.map((c) => c.flat != null);
  for (let i = 0; i < camere.length; i++) {
    camere[i].arieCladita = arieConstruita(
      camere[i].inel, pereti,
      inele.filter((_, j) => j !== i && aleUnuiApartament[j]));
  }

  const rect = plan?.core_rect;
  const nucleu = rect && rect.length === 4
    ? {
        rect: [rect[0], rect[1], rect[2], rect[3]] as
          [number, number, number, number],
        parti: (plan?.core_parts ?? []).map((p) => ({
          kind: p.kind,
          inel: p.poly.map((q) => [q[0], q[1]] as Pt),
        })),
      }
    : null;

  const piese = (plan?.furniture ?? []).map((f) => ({
    kind: f.kind,
    c: [f.x.a, f.y.a] as Pt,
    w: f.w_m ?? 1.6,
    h: f.h_m ?? 1.0,
    inaltime: f.height_m ?? felulPiesei(f.kind)?.inaltime ?? INALTIME_PIESA,
    deg: f.rot_deg ?? 0,
    oglX: f.flip_x === true,
    oglY: f.flip_y === true,
    // COTA TĂLPII, cu aceeași cădere pe catalog ca `inaltime` de mai sus.
    //
    // Era `f.z_m ?? 0`, fără catalog. Importul scrie `z_m` numai pentru ce
    // montează el în blat (plita, chiuveta), iar hota și corpul suspendat nu
    // trec pe-acolo: ieșeau la 0, adică ÎNGROPATE sub blatul de 0,90, în
    // corpurile de jos. Catalogul le știe de mult (hotă 1,50, corp suspendat
    // 1,45, televizor 1,10), numai că nimeni nu-l întreba — deși linia de
    // deasupra îl întreabă pentru înălțimea piesei.
    //
    // AICI, nu în import: repară și documentele deja importate, și e aceeași
    // hotărâre pe care mobilarea automată o ia explicit (`design/auto.ts`:
    // cota așezării dacă o dă, altfel cea a felului).
    z: f.z_m ?? felulPiesei(f.kind)?.z ?? 0,
    /** Rampele, la scara DESENATĂ. Lipsă = scară pusă ca o cutie (sau adusă
     *  din import): se desenează mai departe din `w` × `h`. */
    scara: f.scara,
    grup: f.grup,
    rang: f.rang,
  }));

  // APARATELE ELECTRICE, cu implicitele aplicate. Înălțimea unei plafoniere e
  // chiar înălțimea nivelului, deci implicitul ei nu e o constantă, ci se
  // citește din modul — de-aia `nivel` intră în socoteală.
  const electrice: Electric[] = (plan?.electrical ?? []).map((e, idx) => ({
    idx,
    kind: e.kind,
    c: [e.x.a, e.y.a] as Pt,
    h: e.h_m ?? (e.kind === "doza" ? inaltimeaDozei(e.devices ?? [], nivel)
      : inaltimeaAparatului(e.kind, nivel)),
    deg: e.rot_deg ?? 0,
    circuit: e.circuit ?? circuitulAparatului(e.kind),
    pentru: e.pentru ?? null,
    parte: e.parte ?? null,
    oglX: e.flip_x === true,
    oglY: e.flip_y === true,
    // An empty box has no route even with a circuit written: nothing in it
    // draws current.
    ...(e.kind === "doza" ? {
      devices: e.devices ?? [],
      circuite: !(e.devices ?? []).length ? []
        : e.circuit ? [e.circuit] : circuiteleDozei(e.devices!),
    } : {}),
  }));

  // CADRUL DESENULUI CUPRINDE ȘI CE IESE DIN ANVELOPĂ. Anvelopa e amprenta
  // modulului — banda —, iar un balcon în consolă stă dincolo de ea, cu 1,56 m
  // pe M2. Încadrat pe anvelopă, desenul l-ar fi tăiat de pe hârtie și
  // panoramarea implicită ar fi pornit cu el în afara ecranului.
  const cadru = cadrul([...env, ...camere.filter((c) => c.afara).flatMap((c) => c.inel)]);

  return { anvelopa: env, camere, pereti, goluri, praguri, pervazuri, piese, electrice,
           nucleu, cadru, colt, nivel,
           // COTELE OMULUI și cele stinse de el, duse mai departe ca atare:
           // desenul le pune lângă cele socotite (v. `cote::cotelePlanului`).
           coteDeMana: (plan?.dims ?? []).map(
             (c) => ({ a: [c.a[0], c.a[1]] as Pt, b: [c.b[0], c.b[1]] as Pt })),
           coteStinse: [...(plan?.dims_stinse ?? [])] };
}

/** Câte apartamente și cu câte camere — pentru cartonaș și pentru titlu. */
export function locuinte(doc: TronsonDoc): number[] {
  return ["A", "B"].flatMap((s) => doc.sides[s]?.flats ?? [])
    .map((f) => f.camere);
}

/** Suprafața utilă a variantei implicite, în m². */
export function utilImplicit(doc: TronsonDoc): number {
  const v = doc.variants.find((x) => x.default) ?? doc.variants[0];
  return (v?.useful_m2 ?? []).reduce((a, b) => a + b, 0);
}
